import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  format, parseISO, isWithinInterval, isSameDay,
  startOfMonth, endOfMonth, eachDayOfInterval,
  addMonths, subMonths, isWeekend,
} from 'date-fns';
import {
  Umbrella, Home, Heart, Baby, DollarSign, Thermometer,
  Tag, Cake, Users, Stethoscope, GraduationCap,
  Briefcase, Globe, RefreshCw, Gift,
  ChevronLeft, ChevronRight, ChevronDown, Plus, Trash2, Loader2,
  Download, Search, CalendarDays, Clock, Flag, X,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Avatar } from '../components/ui/Avatar';
import { RequestForm } from '../components/requests/RequestForm';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import {
  toDay, unwrap, isNonWorkingDay, getPublicHoliday, empObservesHolidays, holidaysForEmp,
  fmtHours, fmtDays, deductionForRequest, computeEmployeeLeaveStats, balanceLabels,
  normaliseSchedules, isHalfDayLeave, getHalfDayPeriod,
} from '../utils/leaveBalance';

// Emails hidden from the calendar view
const HIDDEN_EMAILS = ['webadmin@peachloves.co.uk'];

// Weekend columns take this fraction of a weekday column's width, so the
// working days (where the leave actually is) get the room.
const WEEKEND_W = 0.6;
const colFlex = isWk => (isWk ? WEEKEND_W : 1);

function useWindowWidth() {
  const [width, setWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}

const ICON_MAP = {
  'Annual Leave':            Umbrella,
  'Sick Leave':              Thermometer,
  'Remote Work':             Home,
  'Working Remotely':        Home,
  'Working Remotely Abroad': Globe,
  'Unpaid Leave':            DollarSign,
  'Compassionate Leave':     Heart,
  'Compassionate':           Heart,
  'Parental Leave':          Baby,
  'Maternity':               Baby,
  'Paternity':               Baby,
  'Holiday':                 Umbrella,
  'Medical':                 Stethoscope,
  'Meeting':                 Users,
  'Training':                GraduationCap,
  'Birthday':                Cake,
  'Christmas':               Gift,
  'Sick-Deductable':         Thermometer,
  'Day Swap-Working':        RefreshCw,
  'Day Swap- Not Working':   RefreshCw,
  'Di Working':              Briefcase,
};

// ─── Colour helpers for leave bars ───
function hexToRgb(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16));
}

// Mix a colour with white. amount 0 = the colour, 1 = white.
function tint(hex, amount) {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#eadfc6';
  return '#' + rgb.map(c => Math.round(c + (255 - c) * amount).toString(16).padStart(2, '0')).join('');
}

// White on dark leave colours, near-black on light ones (amber, lime…).
function readableOn(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#fff';
  const [r, g, b] = rgb.map(c => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.36 ? '#1c1a14' : '#fff';
}

// Department name, whichever shape the employee record carries it in.
function deptOf(emp) {
  const d =
    emp?.dept ??
    (typeof emp?.department === 'string' ? emp.department : emp?.department?.name) ??
    emp?.departments?.[0]?.departmentName ??
    emp?.departments?.[0]?.name ??
    '';
  return typeof d === 'string' ? d.trim() : '';
}

const NO_DEPT = 'No department';

const COLOR_FALLBACKS = [
  { pending: '#fff4d2', approved: '#ffc81e' },
  { pending: '#fdedcc', approved: '#f5a300' },
  { pending: '#f4e8cc', approved: '#c98a00' },
  { pending: '#f9f8f5', approved: '#e3dccb' },
];

function buildLeaveColors(leaveTypes) {
  return leaveTypes.reduce((acc, type, i) => {
    const approved = type.color || COLOR_FALLBACKS[i % COLOR_FALLBACKS.length].approved;
    // Pending shows as a light tint of the type's own colour, so it stays recognisable
    const pending  = type.pendingColor || tint(approved, 0.8);
    const icon =
      ICON_MAP[type.name] ||
      Object.entries(ICON_MAP).find(([key]) =>
        type.name?.toLowerCase().includes(key.toLowerCase())
      )?.[1] ||
      Tag;
    acc[type.name] = { approved, pending, icon, affectsBalance: type.affectsBalance !== false };
    return acc;
  }, {});
}


function isBirthday(profile, day) {
  if (!profile?.dob) return false;
  try {
    const dob = parseISO(profile.dob);
    return dob.getDate() === day.getDate() && dob.getMonth() === day.getMonth();
  } catch { return false; }
}


function HatchOverlay({ id = 'cal-hatch', color = '#dbcfb0', opacity = 0.3 }) {
  return (
    <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
      <defs>
        <pattern id={id} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke={color} strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} opacity={opacity} />
    </svg>
  );
}

// ─── One day's slice of a leave bar. Consecutive days join into a single
// bar: ends are rounded only where the run starts or stops, and stay flat
// where the leave carries on into the previous / next month.
//   half:  'AM' fills the left half of the day, 'PM' the right half.
//   label: shown once, at the start of a run of three or more days. ───
function LeaveBar({ leave, leaveColors, barTop, barHeight, roundLeft, roundRight, flatLeft, flatRight, half, showIcon, label, labelSpan = 1, onActivate, dimmed = false }) {
  const fallback  = Object.values(leaveColors)[0] || { pending: '#EFDFB8', approved: '#ffc81e', icon: Umbrella };
  const cfg       = leaveColors[leave.type] || fallback;
  const Icon      = cfg.icon;
  const isPending = leave.status === 'pending';
  const fill      = isPending ? cfg.pending : cfg.approved;
  const ink       = isPending ? cfg.approved : readableOn(cfg.approved);

  const halfLabel = half ? ' – half day (' + half + ')' : '';
  const tooltip   = leave.type + halfLabel + (leave.note ? ' – ' + leave.note : '') + ' (' + leave.status + ')';

  const R = half ? 6 : 9;
  const radius = [
    roundLeft  ? R : 0,
    roundRight ? R : 0,
    roundRight ? R : 0,
    roundLeft  ? R : 0,
  ].map(v => v + 'px').join(' ');

  // A little breathing room at the ends of every bar
  let left  = roundLeft  ? 5 : 0;
  let right = roundRight ? 5 : 0;
  if (flatLeft)  left  = 0;
  if (flatRight) right = 0;
  if (half === 'AM') { left = 4; right = 'calc(50% + 2px)'; }
  if (half === 'PM') { left = 'calc(50% + 2px)'; right = 4; }

  const dashed = '1.5px dashed ' + cfg.approved;
  const interactive = !!onActivate;

  return (
    <>
      <div
        className={isPending ? 'cal-bar cal-bar-pending' : 'cal-bar'}
        title={tooltip}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-label={interactive ? tooltip : undefined}
        onKeyDown={interactive ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); } }) : undefined}
        style={{
          position: 'absolute', top: barTop, height: barHeight, left, right,
          background: fill, borderRadius: radius, boxSizing: 'border-box',
          borderTop:    isPending ? dashed : 'none',
          borderBottom: isPending ? dashed : 'none',
          borderLeft:   isPending && roundLeft  ? dashed : 'none',
          borderRight:  isPending && roundRight ? dashed : 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10, cursor: 'pointer',
          opacity: dimmed ? 0.22 : 1,
        }}
      >
        {showIcon && !label && !half && <Icon size={barHeight < 20 ? 11 : 14} color={ink} strokeWidth={2.2} />}
      </div>
      {label && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', top: barTop, height: barHeight, left: 15,
            width: 'calc(' + labelSpan + ' * 100% - 28px)',
            display: 'flex', alignItems: 'center', gap: 7,
            zIndex: 11, pointerEvents: 'none', color: ink,
            opacity: dimmed ? 0.22 : 1, transition: 'opacity 0.15s',
          }}
        >
          <Icon size={barHeight < 20 ? 11 : 14} color={ink} strokeWidth={2.2} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: barHeight < 20 ? 11 : 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1 }}>{label}</span>
        </div>
      )}
    </>
  );
}

function LeaveDetailPopup({ leave, emp, currentUser, leaveColors, onClose, onDelete, deleting, onRequestOtherHalf, otherHalfAlreadyBooked }) {
  // Escape closes the popup
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  otherHalfAlreadyBooked = otherHalfAlreadyBooked || false;
  const isPrivileged = ['admin', 'super_admin'].includes(currentUser?.role);
  const isOwner      = (leave.empId ?? leave.userId) === currentUser?.id;
  const canDelete    = isOwner || isPrivileged;

  const fallback = Object.values(leaveColors)[0] || { approved: '#ffc81e', pending: '#fff4d2', icon: Umbrella };
  const cfg      = leaveColors[leave.type] || fallback;
  const Icon     = cfg.icon;
  const iconBg   = leave.status === 'pending' ? cfg.pending : cfg.approved;
  const iconFg   = leave.status === 'pending' ? cfg.approved : readableOn(cfg.approved);

  const isHalf   = isHalfDayLeave(leave);
  const period   = isHalf ? getHalfDayPeriod(leave) : null;
  const isCustom = leave.period === 'custom';

  const statusStyle = {
    pending:  { bg: 'var(--warn-bg-soft)', color: 'var(--warn-text)', border: 'var(--warn-border)' },
    approved: { bg: 'var(--ok-bg)', color: 'var(--ok-text)', border: 'var(--ok-border)' },
    rejected: { bg: 'var(--bad-bg)', color: 'var(--bad-text)', border: 'var(--bad-border)' },
  }[leave.status] || { bg: 'var(--border-soft)', color: 'var(--text-2)', border: 'var(--border)' };

  const daysDisplay = isHalf
    ? (period ? 'half day (' + period + ')' : 'half day')
    : isCustom
      ? (leave.hours != null ? parseFloat(leave.hours) : '?') + 'h'
      : (leave.days ?? '-');

  const canRequestOtherHalf =
    isHalf && period !== null && !otherHalfAlreadyBooked &&
    (isOwner || isPrivileged || currentUser?.role === 'manager');
  const otherHalfPeriod = period === 'AM' ? 'PM' : 'AM';

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'var(--overlay)' }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 201,
        width: 'min(340px, calc(100vw - 32px))',
        background: 'var(--surface)', borderRadius: 18,
        border: '1px solid var(--border)',
        boxShadow: '0 24px 70px rgba(28,26,20,0.22)',
        padding: 20, paddingTop: 24, fontFamily: 'inherit', overflow: 'hidden',
        animation: 'popupIn 0.22s cubic-bezier(0.22, 1, 0.36, 1) both',
      }}>
        {/* Band in the leave type's colour */}
        <div aria-hidden="true" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 5, background: cfg.approved, opacity: leave.status === 'pending' ? 0.45 : 1 }} />
        <button onClick={onClose} aria-label="Close" className="cal-popup-x">
          <X size={15} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, paddingRight: 30 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
            <Icon size={17} color={iconFg} strokeWidth={2.5} />
            {isHalf && (
              <div style={{ position: 'absolute', top: period === 'PM' ? 0 : '50%', bottom: period === 'PM' ? '50%' : 0, left: 0, right: 0, background: 'rgba(255,255,255,0.35)', pointerEvents: 'none' }} />
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{leave.type}</p>
              {isHalf && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: 'var(--warn-bg)', color: 'var(--warn-text)', border: '1px solid var(--warn-border)', flexShrink: 0 }}>
                  half day
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
              <span style={{ fontSize: 12.5, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp?.name}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: statusStyle.bg, color: statusStyle.color, border: '1px solid ' + statusStyle.border, flexShrink: 0, textTransform: 'capitalize' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusStyle.color }} />
                {leave.status}
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--surface-2)', marginBottom: 14 }}>
          {[
            { label: 'From', val: format(toDay(leave.from) ?? new Date(), 'EEE, dd MMM') },
            !isHalf && !isCustom && { label: 'To', val: format(toDay(leave.to ?? leave.from) ?? new Date(), 'EEE, dd MMM') },
            isCustom && { label: 'Time', val: (leave.startTime ?? '?') + '-' + (leave.endTime ?? '?') },
            { label: isCustom ? 'Hours' : isHalf ? 'Hours' : 'Days', val: daysDisplay },
          ].filter(Boolean).map((col, i, arr) => (
            <div key={col.label} style={{ flex: 1, padding: '10px 12px', borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <p style={{ margin: 0, fontSize: 10.5, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{col.label}</p>
              <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{col.val}</p>
            </div>
          ))}
        </div>

        {leave.note && (
          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-2)', background: 'var(--surface)', borderLeft: '3px solid ' + cfg.approved, borderRadius: 4, padding: '6px 12px', lineHeight: 1.5 }}>
            “{leave.note}”
          </p>
        )}

        {leave.submitter && (
          <p style={{ margin: '0 0 14px', fontSize: 11, color: 'var(--text-faint)' }}>
            Submitted by {leave.submitter.firstName} {leave.submitter.lastName}
          </p>
        )}

        {isCustom && leave.startTime && leave.endTime && (
          <div style={{ marginBottom: 14, padding: '6px 10px', borderRadius: 6, background: 'var(--ok-bg)', border: '1px solid var(--ok-border)', fontSize: 12, color: 'var(--ok-text)', fontWeight: 500 }}>
            Custom hours: {leave.startTime} - {leave.endTime} - {leave.hours != null ? parseFloat(leave.hours) : '?'}h
            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 20, background: 'var(--border-soft)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
              No balance impact
            </span>
          </div>
        )}

        {canRequestOtherHalf && (
          <button
            onClick={() => onRequestOtherHalf(otherHalfPeriod)}
            style={{ width: '100%', marginBottom: 8, padding: '9px 12px', borderRadius: 8, border: '1.5px dashed var(--tint-strong)', background: 'var(--tint)', color: 'var(--accent)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--tint-strong)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--tint)'}
          >
            <Plus size={13} />
            Request {otherHalfPeriod} half
          </button>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          {canDelete && (
            <button
              onClick={onDelete}
              disabled={deleting}
              className="cal-popup-delete"
              style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid var(--bad-border)', background: 'var(--surface)', color: 'var(--bad-text)', fontSize: 13, fontWeight: 600, cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'background 0.15s' }}
            >
              {deleting
                ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Deleting...</>
                : <><Trash2 size={13} /> Delete</>
              }
            </button>
          )}
          <button
            onClick={onClose}
            className="cal-popup-close"
            style={{ flex: canDelete ? '0 0 auto' : 1, padding: '10px 20px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-2)', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s' }}
          >
            Close
          </button>
        </div>

        {!canDelete && !canRequestOtherHalf && (
          <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--text-faint)', textAlign: 'center' }}>View only</p>
        )}
      </div>
    </>
  );
}

// ─── Month-grid calendar rendered inside the hover card. `compact` renders a
// tiny, label-free version (used 12-up in the year grid below); the full
// version (day numbers + weekday header) is used when more space allows. ───
function MiniMonthCalendar({ viewDate, empReqs, publicHolidays, empScheds, observesPublicHolidays, leaveColors, deductOpts, compact = false }) {
  const monthDays = useMemo(
    () => eachDayOfInterval({ start: startOfMonth(viewDate), end: endOfMonth(viewDate) }),
    [viewDate]
  );
  const firstWeekdayIdx = (() => {
    const jsDay = startOfMonth(viewDate).getDay(); // 0 = Sun
    return jsDay === 0 ? 6 : jsDay - 1; // Monday-first index
  })();

  function findLeaveForDay(day) {
    return empReqs.find(r => {
      if (r.status === 'rejected' || r.status === 'cancelled') return false;
      const from = toDay(r.from);
      const to   = toDay(r.to ?? r.from);
      if (!from || !to) return false;
      try { return isWithinInterval(day, { start: from, end: to }); }
      catch { return false; }
    }) ?? null;
  }

  const gap = compact ? 1.5 : 3;

  return (
    <div>
      {!compact && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap, marginBottom: 3 }}>
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
            <div key={i} style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, color: 'var(--text-faint)' }}>{d}</div>
          ))}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap }}>
        {Array.from({ length: firstWeekdayIdx }).map((_, i) => <div key={'blank-' + i} />)}
        {monthDays.map(day => {
          const isWk          = isWeekend(day);
          const isToday        = isSameDay(day, new Date());
          const ph             = getPublicHoliday(publicHolidays, day);
          const isBankHoliday  = !!ph && observesPublicHolidays;
          const isNonWork      = !isWk && !isBankHoliday && isNonWorkingDay(empScheds, day);
          const leave          = findLeaveForDay(day);
          const isPending      = leave?.status === 'pending';
          const cfg            = leave ? (leaveColors[leave.type] || Object.values(leaveColors)[0]) : null;
          const isHalf         = leave ? isHalfDayLeave(leave) : false;
          const period         = isHalf ? getHalfDayPeriod(leave) : null;
          const deduction      = leave ? deductionForRequest(leave, empScheds, deductOpts) : null;

          let bg = 'var(--surface)';
          let fg = 'var(--text-muted)';
          if (isWk)         { bg = 'var(--border-soft)'; fg = 'var(--text-faint)'; }
          if (isNonWork)    { bg = 'var(--border-soft)'; fg = 'var(--text-faint)'; }
          if (isBankHoliday){ bg = 'var(--holiday-strong)'; fg = '#1C1A14'; }

          const title = [
            format(day, 'EEE, d MMM yyyy'),
            ph ? `Bank holiday: ${ph.name}` : null,
            isNonWork ? 'Non-working day' : null,
            leave ? `${leave.type}${isHalf ? ` (${period || 'half'} day)` : ''} — ${leave.status}${deduction ? ` — ${fmtHours(deduction.hours)} for the whole request` : ''}` : null,
          ].filter(Boolean).join(' · ');

          return (
            <div
              key={day.toISOString()}
              title={title}
              style={{
                position: 'relative', aspectRatio: '1', borderRadius: compact ? 2 : 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: compact ? 0 : 10, fontWeight: 600, background: bg,
                border: isToday ? (compact ? '1px solid var(--accent)' : '1.5px solid var(--accent)') : '1px solid transparent',
                overflow: 'hidden', cursor: 'default',
              }}
            >
              {leave && !isHalf && (
                <div style={{ position: 'absolute', inset: 0, background: isPending ? cfg?.pending : cfg?.approved }} />
              )}
              {leave && isHalf && (
                <>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: period === 'AM' ? (isPending ? cfg?.pending : cfg?.approved) : 'transparent' }} />
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '50%', background: period === 'PM' ? (isPending ? cfg?.pending : cfg?.approved) : 'transparent' }} />
                </>
              )}
              {isBankHoliday && !leave && !compact && (
                <Flag size={9} style={{ position: 'absolute', top: 2, right: 2, color: '#1C1A14' }} strokeWidth={2.5} />
              )}
              {!compact && (
                <span style={{ position: 'relative', zIndex: 1, color: leave && !isHalf ? readableOn(isPending ? cfg?.pending : cfg?.approved) : fg }}>
                  {format(day, 'd')}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Admin/manager hover summary card. Positioned via fixed coords
// derived from the hovered row's bounding rect (computed in the parent),
// clamped to stay on-screen. Reads from computeEmployeeLeaveStats so its
// numbers always match the badge shown on the row itself. Shows a full mini
// calendar (all holidays/leave for the month, nothing truncated) and, when
// the employee's allowance is tracked in hours, formats every figure — the
// summary row, the pending banner, and each list item — in hours. For
// South Africa Team members (no balance tracked) the Entitled/Used/Left
// tiles show a dash instead of a figure. ───
function EmployeeHoverCard({ emp, stats, leaveColors, publicHolidays, viewDate, top, left, onMouseEnter, onMouseLeave, onDownload }) {
  // This card browses its own month independently of the main
  // calendar — starts on whatever month the main calendar is showing,
  // then can be paged with the arrows below or the ← / → keys.
  const [cardMonth, setCardMonth] = useState(startOfMonth(viewDate));
  const [showDl, setShowDl] = useState(false);

  // The `stats` prop only covers the main calendar's currently
  // viewed year (that's all the parent has already fetched). If the card
  // is paged into a different year, its Entitled/Used/Left figures, the
  // leave on the mini calendar and that year's bank holidays must come from
  // THAT year, so lazily fetch per year and cache for the life of this hover.
  const baseYear = viewDate.getFullYear();
  const cardYear = cardMonth.getFullYear();
  const onBaseYear = cardYear === baseYear;
  const [yearCache, setYearCache] = useState({}); // { [year]: { allowance, requests, holidays } }
  const [yearLoading, setYearLoading] = useState(false);

  useEffect(() => {
    if (onBaseYear || yearCache[cardYear]) return;
    let cancelled = false;
    setYearLoading(true);
    Promise.all([
      apiService.getEmployeeAllowance(emp.id, cardYear).catch(() => null),
      apiService.getLeaveRequests({
        scope: 'all',
        limit: 1000,
        from: `${cardYear}-01-01`,
        to: `${cardYear}-12-31`,
      }).catch(() => null),
      apiService.getPublicHolidays(cardYear).catch(() => null),
    ]).then(([allowanceRes, requestsRes, holidaysRes]) => {
      if (cancelled) return;
      const allowance = unwrap(allowanceRes);
      const list = requestsRes?.data?.requests ?? requestsRes?.data ?? requestsRes ?? [];
      const holidays = unwrap(holidaysRes);
      setYearCache(prev => ({
        ...prev,
        [cardYear]: {
          allowance,
          requests: Array.isArray(list) ? list : [],
          holidays: Array.isArray(holidays) ? holidays : [],
        },
      }));
    }).finally(() => { if (!cancelled) setYearLoading(false); });
    return () => { cancelled = true; };
  }, [cardYear, onBaseYear, emp.id]); // eslint-disable-line

  // Only their holiday location's public holidays
  const holidaysForCard = onBaseYear
    ? (stats?.deductOpts?.publicHolidays ?? holidaysForEmp(publicHolidays, null, emp.id))
    : holidaysForEmp(yearCache[cardYear]?.holidays ?? [], yearCache[cardYear]?.allowance, emp.id);

  // The stats actually displayed: the parent-supplied ones when browsing
  // the base year (no extra fetch needed), or freshly computed ones for
  // whichever other year has been navigated to and finished loading.
  const yearStats = useMemo(() => {
    if (onBaseYear) return stats;
    const cached = yearCache[cardYear];
    if (!cached) return null; // still loading
    return computeEmployeeLeaveStats(
      emp,
      cached.requests,
      cached.allowance ? { [emp.id]: cached.allowance } : {},
      { [emp.id]: stats.empScheds }, // schedules carry their own effective dates — safe to reuse
      leaveColors,
      cardYear,
      cached.holidays
    );
  }, [onBaseYear, cardYear, yearCache, stats, emp, leaveColors]);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); setCardMonth(d => subMonths(d, 1)); }
      if (e.key === 'ArrowRight') { e.preventDefault(); setCardMonth(d => addMonths(d, 1)); }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const fmtDateShort = d => {
    const day = toDay(d);
    return day ? format(day, 'd MMM') : d;
  };
  const inHours = (yearStats ?? stats).displayUnit === 'hours';
  // Formats a stat in whichever unit this employee's allowance actually
  // tracks — hours everywhere if displayUnit is 'hours', days otherwise
  const fmtStat = (hoursVal, daysVal) => (inHours ? fmtHours(hoursVal) : fmtDays(daysVal));
  const noBalance = (yearStats ?? stats)?.noBalanceTracking;

  const history = (yearStats?.empReqs || [])
    .filter(r => r.status !== 'rejected' && r.status !== 'cancelled')
    .sort((a, b) => String(b.from).localeCompare(String(a.from)));
  const shown = history.slice(0, 6);
  const extraCount = history.length - shown.length;

  const isCardMonthCurrent =
    cardMonth.getMonth() === new Date().getMonth() && cardMonth.getFullYear() === new Date().getFullYear();
  const isLoadingYear = !onBaseYear && !yearStats;
  const overdrawn = yearStats && !noBalance && (inHours ? yearStats.remainingHours : yearStats.remainingDays) < 0;

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'fixed',
        top, left,
        zIndex: 300,
        width: 310,
        maxHeight: 'min(580px, calc(100vh - 24px))',
        background: 'var(--surface)',
        borderRadius: 14,
        border: '1px solid var(--border)',
        boxShadow: '0 24px 48px rgba(15,23,42,0.16), 0 6px 16px rgba(15,23,42,0.08)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        animation: 'hoverCardIn 0.15s cubic-bezier(0.22, 1, 0.36, 1) both',
      }}
    >
      <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--border-soft)', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface-2)', flexShrink: 0 }}>
        <Avatar employee={emp} size="sm" />
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp.name}</p>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>{deptOf(emp) || NO_DEPT}</p>
        </div>
        {onDownload && (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              type="button"
              className="cal-hc-dl"
              onClick={() => setShowDl(v => !v)}
              aria-haspopup="menu"
              aria-expanded={showDl}
              title={`Download ${emp.name?.split(' ')[0] ?? 'their'}'s leave`}
              aria-label={`Download ${emp.name}'s leave`}
              disabled={!yearStats}
            >
              <Download size={14} />
            </button>
            {showDl && yearStats && (
              <div role="menu" className="cal-menu" style={{ minWidth: 190, top: 'calc(100% + 4px)' }}>
                {[
                  { range: 'month', label: format(cardMonth, 'MMMM yyyy') },
                  { range: 'year',  label: `All of ${cardYear}` },
                ].map(opt => (
                  <button
                    key={opt.range}
                    role="menuitem"
                    className="cal-menu-item"
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    onClick={() => { setShowDl(false); onDownload(emp, opt.range, cardMonth, yearStats.empReqs, yearStats); }}
                  >
                    <Download size={13} style={{ color: 'var(--accent)' }} /> {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {!isLoadingYear && noBalance ? (
        <div style={{ margin: '12px 14px 0', padding: '8px 10px', borderRadius: 8, background: 'var(--surface-2)', border: '1px solid var(--border)', fontSize: 11, color: 'var(--text-2)', fontWeight: 500, flexShrink: 0 }}>
          South Africa Team — leave isn't tracked against a balance
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: 'var(--border-soft)', flexShrink: 0 }}>
          {[
            { label: 'Entitled', val: yearStats ? fmtStat(yearStats.totalHours, yearStats.totalDays)         : '…', color: 'var(--text)' },
            { label: 'Used',     val: yearStats ? fmtStat(yearStats.used.hours, yearStats.used.days)         : '…', color: 'var(--bad-text)' },
            { label: 'Left',     val: yearStats ? fmtStat(yearStats.remainingHours, yearStats.remainingDays) : '…', color: overdrawn ? 'var(--bad-text)' : 'var(--ok-text)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--surface)', padding: '10px 6px', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: s.color, opacity: isLoadingYear ? 0.4 : 1 }}>{s.val}</p>
              <p style={{ margin: '2px 0 0', fontSize: 9.5, color: 'var(--text-faint)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {yearStats && !noBalance && (inHours ? yearStats.pending.hours : yearStats.pending.days) > 0 && (
        <div style={{ margin: '10px 14px 0', padding: '6px 10px', borderRadius: 8, background: 'var(--warn-bg-soft)', border: '1px solid var(--warn-border)', fontSize: 11, color: 'var(--warn-text)', fontWeight: 600, flexShrink: 0 }}>
          {fmtStat(yearStats.pending.hours, yearStats.pending.days)} pending approval
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px 14px' }}>
        {/* Month navigator — arrows + ← / → keys page through months,
        and years (the card re-fetches this employee's data for whichever
        year you land on, so the stats above always match what's shown) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <button
            onClick={() => setCardMonth(d => subMonths(d, 1))}
            style={{ width: 22, height: 22, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--text-muted)', flexShrink: 0 }}
            aria-label="Previous month"
          >
            <ChevronLeft size={13} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: isCardMonthCurrent ? 'var(--accent)' : 'var(--text)' }}>
              {format(cardMonth, 'MMMM yyyy')}
            </p>
            {isCardMonthCurrent && <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />}
            {(isLoadingYear || yearLoading) && <Loader2 size={11} style={{ color: 'var(--text-faint)', animation: 'spin 1s linear infinite' }} />}
          </div>
          <button
            onClick={() => setCardMonth(d => addMonths(d, 1))}
            style={{ width: 22, height: 22, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--text-muted)', flexShrink: 0 }}
            aria-label="Next month"
          >
            <ChevronRight size={13} />
          </button>
        </div>

        <MiniMonthCalendar
          viewDate={cardMonth}
          empReqs={yearStats?.empReqs ?? []}
          publicHolidays={holidaysForCard}
          empScheds={yearStats?.empScheds ?? stats.empScheds}
          observesPublicHolidays={yearStats?.observesPublicHolidays ?? stats.observesPublicHolidays}
          leaveColors={leaveColors}
          deductOpts={yearStats?.deductOpts ?? stats.deductOpts}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, fontSize: 9.5, color: 'var(--text-faint)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--holiday-strong)', display: 'inline-block' }} />
            Bank holiday
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--border-soft)', border: '1px solid var(--field-border)', display: 'inline-block' }} />
            Non-working
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: Object.values(leaveColors)[0]?.approved ?? '#ffc81e', display: 'inline-block' }} />
            Leave
          </span>
        </div>

        <p style={{ margin: '16px 0 8px', fontSize: 10, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Leave history — {cardYear}
        </p>
        {isLoadingYear ? (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-faint)' }}>Loading {cardYear}…</p>
        ) : shown.length === 0 ? (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-faint)' }}>No leave requests this year</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {shown.map((r, i) => {
              const cfg        = leaveColors[r.type] || Object.values(leaveColors)[0] || { approved: '#ffc81e', pending: '#fff4d2', icon: Umbrella };
              const Icon       = cfg.icon;
              const isPending  = r.status === 'pending';
              const deduction  = deductionForRequest(r, yearStats.empScheds, yearStats.deductOpts);
              return (
                <div key={r.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 22, height: 22, borderRadius: 6, background: isPending ? cfg.pending : cfg.approved, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={11} color={isPending ? cfg.approved : readableOn(cfg.approved)} strokeWidth={2.5} />
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ margin: 0, fontSize: 11.5, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.type}
                    </p>
                    <p style={{ margin: 0, fontSize: 10.5, color: 'var(--text-muted)' }}>
                      {fmtDateShort(r.from)}{r.to && String(r.to).slice(0, 10) !== String(r.from).slice(0, 10) ? ` – ${fmtDateShort(r.to)}` : ''}
                      {noBalance ? '' : ` · ${fmtDays(deduction.days)} (${fmtHours(deduction.hours)})`}
                    </p>
                  </div>
                  <span style={{ fontSize: 9.5, fontWeight: 700, padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0, background: isPending ? 'var(--warn-bg)' : 'var(--ok-bg)', color: isPending ? 'var(--warn-text)' : 'var(--ok-text)' }}>
                    {r.status}
                  </span>
                </div>
              );
            })}
            {extraCount > 0 && (
              <p style={{ margin: '4px 0 0', fontSize: 10.5, color: 'var(--text-faint)', textAlign: 'center' }}>+{extraCount} more</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CSV export helpers ───
// Quote every cell, and neutralise values a spreadsheet would run as a formula.
function csvCell(value) {
  let text = value == null ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = "'" + text;
  return '"' + text.replace(/"/g, '""') + '"';
}

function downloadCsv(filename, rows) {
  const csv  = rows.map(row => row.map(csvCell).join(',')).join('\r\n');
  // BOM so Excel reads accented names correctly
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function slugify(text) {
  return String(text ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'export';
}

const fmtNum = n => (n == null || isNaN(n) ? '' : Math.round(n * 100) / 100);
const statusLabel = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

// Working days and hours a request uses inside [start, end] — the same
// rules as the balance (schedule hours, bank holidays, clipping to the period)
function deductionInRange(r, empScheds, start, end, opts = {}) {
  return deductionForRequest(r, empScheds, { ...opts, rangeStart: start, rangeEnd: end });
}

// The period an export covers: the given month, or January–December
function exportPeriod(range, monthDate) {
  const year = monthDate.getFullYear();
  if (range === 'year') {
    return { start: new Date(year, 0, 1), end: new Date(year, 11, 31, 23, 59, 59), label: String(year), fileTag: String(year) };
  }
  return { start: startOfMonth(monthDate), end: endOfMonth(monthDate), label: format(monthDate, 'MMMM yyyy'), fileTag: format(monthDate, 'yyyy-MM') };
}

// Approved and pending requests for one person that touch the period, oldest first
function requestsInPeriod(empId, requests, period) {
  return requests
    .filter(r => (r.empId ?? r.userId) === empId)
    .filter(r => r.status !== 'rejected' && r.status !== 'cancelled')
    .filter(r => {
      const from = toDay(r.from);
      const to   = toDay(r.to ?? r.from);
      if (!from || !to) return false;
      return !(to < period.start || from > period.end);
    })
    .sort((a, b) => String(a.from).localeCompare(String(b.from)));
}

function requestRow(r, empScheds, period, extraLead = [], opts = {}) {
  const from = toDay(r.from);
  const to   = toDay(r.to ?? r.from) ?? from;
  const half = isHalfDayLeave(r);
  const period_ = getHalfDayPeriod(r);
  const used = deductionInRange(r, empScheds, period.start, period.end, opts);
  return [
    ...extraLead,
    r.type ?? r.leaveType?.name ?? '',
    from ? format(from, 'dd/MM/yyyy') : '',
    to ? format(to, 'dd/MM/yyyy') : '',
    half ? `Half day${period_ ? ` (${period_})` : ''}` : r.period === 'custom' ? 'Custom hours' : 'Full day',
    fmtNum(used.days),
    fmtNum(used.hours),
    statusLabel(r.status),
    r.note ?? '',
  ];
}

// ─── One person's leave for a month or a year, as a CSV with a short
// summary block on top (balance figures only for years, and only for
// people whose balance is tracked). Returns the number of requests. ───
function exportPersonLeave({ emp, requests, empScheds, stats, leaveColors, range, monthDate }) {
  const period = exportPeriod(range, monthDate);
  const reqs = requestsInPeriod(emp.id, requests, period);
  const opts = {
    publicHolidays: stats?.deductOpts?.publicHolidays ?? [],
    observesPH:     stats?.observesPublicHolidays ?? false,
  };

  const isDeductible = r => leaveColors[r.type]?.affectsBalance !== false;
  const total = (list) => list.reduce((acc, r) => {
    const d = deductionInRange(r, empScheds, period.start, period.end, opts);
    return { days: acc.days + d.days, hours: acc.hours + d.hours };
  }, { days: 0, hours: 0 });
  const approved = total(reqs.filter(r => r.status === 'approved'));
  const pending  = total(reqs.filter(r => r.status === 'pending'));
  const approvedDeductible = total(reqs.filter(r => r.status === 'approved' && isDeductible(r)));

  const rows = [
    ['Employee', emp.name ?? ''],
    ['Department', deptOf(emp) || NO_DEPT],
    ['Period', period.label],
    ['Approved leave (working days)', fmtNum(approved.days)],
    ['Pending leave (working days)', fmtNum(pending.days)],
  ];
  if (range === 'year' && stats && !stats.noBalanceTracking) {
    const inHours = stats.displayUnit === 'hours';
    const unit = inHours ? 'hours' : 'days';
    rows.push(
      [`Entitlement (${unit})`, fmtNum(inHours ? stats.totalHours : stats.totalDays)],
      [`Used from allowance (${unit})`, fmtNum(inHours ? stats.used.hours : stats.used.days)],
      [`Remaining (${unit})`, fmtNum(inHours ? stats.remainingHours : stats.remainingDays)],
    );
  } else if (range === 'month') {
    rows.push(['Used from allowance (working days)', fmtNum(approvedDeductible.days)]);
  }
  rows.push([]);
  rows.push(['Leave type', 'From', 'To', 'Duration', 'Working days', 'Hours', 'Status', 'Note']);
  reqs.forEach(r => rows.push(requestRow(r, empScheds, period, [], opts)));
  if (reqs.length === 0) rows.push(['No leave booked in this period']);

  downloadCsv(`leave-${slugify(emp.name)}-${period.fileTag}.csv`, rows);
  return reqs.length;
}

// ─── Everyone's leave for a month or a year: one row per request ───
function exportTeamLeave({ emps, requests, schedulesMap, allowancesMap, publicHolidays, range, monthDate, deptName }) {
  const period = exportPeriod(range, monthDate);
  const rows = [];
  [...emps]
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
    .forEach(emp => {
      const empScheds = schedulesMap[emp.id] ?? [];
      const opts = { publicHolidays: holidaysForEmp(publicHolidays, allowancesMap[emp.id], emp.id), observesPH: empObservesHolidays(allowancesMap[emp.id]) };
      requestsInPeriod(emp.id, requests, period).forEach(r => {
        rows.push(requestRow(r, empScheds, period, [emp.name ?? '', deptOf(emp)], opts));
      });
    });
  if (rows.length === 0) return 0;
  downloadCsv(
    `leave-calendar-${period.fileTag}${deptName ? '-' + slugify(deptName) : ''}.csv`,
    [['Employee', 'Department', 'Leave type', 'From', 'To', 'Duration', 'Working days', 'Hours', 'Status', 'Note'], ...rows]
  );
  return rows.length;
}

// ─── One row per person: totals for the period, split by leave type,
// plus the balance for year exports. Good for payroll / HR sign-off. ───
function exportTeamSummary({ emps, requests, schedulesMap, allowancesMap, leaveColors, publicHolidays, range, monthDate, deptName }) {
  const period = exportPeriod(range, monthDate);
  const typeNames = Object.keys(leaveColors);
  const rows = [...emps]
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
    .map(emp => {
      const empScheds = schedulesMap[emp.id] ?? [];
      const opts = { publicHolidays: holidaysForEmp(publicHolidays, allowancesMap[emp.id], emp.id), observesPH: empObservesHolidays(allowancesMap[emp.id]) };
      const reqs = requestsInPeriod(emp.id, requests, period);
      const byType = {};
      let approvedDays = 0, pendingDays = 0;
      reqs.forEach(r => {
        const d = deductionInRange(r, empScheds, period.start, period.end, opts).days;
        if (r.status === 'approved') {
          approvedDays += d;
          byType[r.type] = (byType[r.type] ?? 0) + d;
        } else if (r.status === 'pending') {
          pendingDays += d;
        }
      });
      const base = [emp.name ?? '', deptOf(emp), fmtNum(approvedDays), fmtNum(pendingDays)];
      if (range === 'year') {
        const st = computeEmployeeLeaveStats(emp, requests, allowancesMap, schedulesMap, leaveColors, monthDate.getFullYear(), publicHolidays);
        if (st.noBalanceTracking) base.push('Not tracked', '', '', '');
        else {
          const inHours = st.displayUnit === 'hours';
          base.push(
            inHours ? 'Hours' : 'Days',
            fmtNum(inHours ? st.totalHours : st.totalDays),
            fmtNum(inHours ? st.used.hours : st.used.days),
            fmtNum(inHours ? st.remainingHours : st.remainingDays),
          );
        }
      }
      return [...base, ...typeNames.map(t => fmtNum(byType[t] ?? 0))];
    });
  const head = ['Employee', 'Department', 'Approved (working days)', 'Pending (working days)'];
  if (range === 'year') head.push('Allowance unit', 'Entitlement', 'Used', 'Remaining');
  downloadCsv(
    `leave-summary-${period.fileTag}${deptName ? '-' + slugify(deptName) : ''}.csv`,
    [[...head, ...typeNames.map(t => `${t} (days)`)], ...rows]
  );
  return rows.length;
}

function buildDayLeaveMap(empRequests, days) {
  const map = {};
  empRequests.forEach(leave => {
    if (leave.status === 'rejected' || leave.status === 'cancelled') return;
    const from = toDay(leave.from);
    const to   = toDay(leave.to ?? leave.from);
    if (!from || !to) return;
    const matchingDays = days.filter(day => {
      try { return isWithinInterval(day, { start: from, end: to }); }
      catch { return false; }
    });
    matchingDays.forEach((day, i) => {
      const isFirst = i === 0;
      const isLast  = i === matchingDays.length - 1;
      let position  = 'mid';
      if (isFirst && isLast) position = 'single';
      else if (isFirst)      position = 'start';
      else if (isLast)       position = 'end';
      const k = day.toISOString();
      if (!map[k]) map[k] = [];
      map[k].push({ leave, position });
    });
  });
  return map;
}

export default function Calendar() {
  const { employees, currentUser } = useApp();
  const windowWidth = useWindowWidth();
  const scrollRef   = useRef(null);

  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;

  // ─── Row density. 'compact' fits the whole team on screen like a wall
  // chart; 'comfortable' gives every row more breathing room. Remembered
  // per browser. ───
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('cal-density-v2') || 'fit'; } catch { return 'fit'; }
  });
  useEffect(() => {
    try { localStorage.setItem('cal-density-v2', density); } catch { /* storage unavailable */ }
  }, [density]);
  // 'fit' shrinks rows so the whole team is on screen at once (wall chart).
  // Phones scroll the page instead, so there it behaves like compact.
  const fitMode = density === 'fit' && !isMobile;
  const compact = density === 'compact' || density === 'fit';
  // Row height that makes everyone fit — measured further down
  const [fitRowH, setFitRowH] = useState(44);
  const keybarRef = useRef(null);

  // Wide day columns with narrower weekends (WEEKEND_W) so weekdays get the
  // space. Columns stretch on wide screens and scroll sideways (opening on
  // today) when the month doesn't fit.
  const LEFT_COL   = isMobile ? 128 : isTablet ? 212 : 252;
  const ROW_HEIGHT = fitMode ? fitRowH : compact ? 50 : (isMobile ? 64 : 76);
  const CELL_MIN   = isMobile ? 48 : isTablet ? 44 : 40;
  const HEADER_H   = fitMode ? 58 : compact ? (isMobile ? 58 : 64) : (isMobile ? 66 : 76);
  const GROUP_H    = fitMode ? 26 : compact ? 34 : 46;
  const BAR_H      = fitMode ? Math.max(14, Math.min(24, ROW_HEIGHT - 12)) : compact ? 24 : (isMobile ? 28 : 30);
  const BAR_TOP    = (ROW_HEIGHT - BAR_H) / 2;
  // What fits in a row at this height
  const showSubline = ROW_HEIGHT >= 40;            // "18d left · 2d pending"
  const showAvatar  = !isMobile && ROW_HEIGHT >= 34;
  const tinyRows    = ROW_HEIGHT < 34;
  const microRows   = ROW_HEIGHT < 26;

  const [viewDate,      setViewDate]    = useState(startOfMonth(new Date()));
  const [showForm,      setShowForm]    = useState(false);
  const [prefill,       setPrefill]     = useState(null);
  const [deptFilter,    setDeptFilter]  = useState('All');
  const [showDeptMenu,  setShowDeptMenu]= useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  // Export menu choices: whose leave ('all' or an employee id) and in what shape
  const [exportWho,  setExportWho]  = useState('all');
  const [exportKind, setExportKind] = useState('requests'); // 'requests' | 'summary'
  const [query,         setQuery]       = useState('');
  const deptMenuRef = useRef(null);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [deletingId,    setDeletingId]    = useState(null);
  const [leaveColors,      setLeaveColors]     = useState({});
  const [allowancesMap,    setAllowances]       = useState({});
  const [schedulesMap,     setSchedules]        = useState({});
  const [profilesMap,      setProfiles]         = useState({});
  const [publicHolidays,   setPublicHolidays]   = useState([]);
  const [calendarRequests, setCalendarRequests] = useState([]);
  // The year whose allowances, schedules and requests are loaded. Balances
  // are only shown once this matches the year in view, so nobody sees the
  // 116h placeholder (or last year's figures) while data is loading.
  const [loadedYear, setLoadedYear] = useState(null);
  const [slideDir, setSlideDir] = useState(null);
  // Clicking a type in the key highlights just that type on the grid
  const [focusType, setFocusType] = useState(null);
  // Clicking the "pending" chip fades everything that isn't waiting for approval
  const [statusFocus, setStatusFocus] = useState(null);
  // Month / year jump menu on the title
  const [showMonthMenu, setShowMonthMenu] = useState(false);
  const [pickYear,      setPickYear]      = useState(() => new Date().getFullYear());
  const monthMenuRef = useRef(null);
  const searchRef    = useRef(null);
  // The grid card is sized to the window so the day header stays in view
  // while the team scrolls inside it, like a wall chart
  const gridCardRef = useRef(null);
  const [gridMaxH, setGridMaxH] = useState(null);
  // The key only lists types in use this month unless expanded
  const [showAllTypes, setShowAllTypes] = useState(false);
  // Department groups the user has folded away
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [animKey,  setAnimKey]  = useState(0);

  // Hover-card state for the admin "see their calendar summary" feature
  const [hoveredRowId, setHoveredRowId] = useState(null);      // instant row highlight
  const [hoveredEmp,   setHoveredEmp]   = useState(null);      // { emp, top, left } — delayed rich card
  const hoverShowTimer = useRef(null);
  const hoverHideTimer = useRef(null);

  const days = useMemo(() =>
    eachDayOfInterval({ start: startOfMonth(viewDate), end: endOfMonth(viewDate) }),
    [viewDate]
  );

  // Derive the current view year for balance calculations
  const viewYear = viewDate.getFullYear();
  const balancesReady = loadedYear === viewYear;

  const isPrivilegedUser = ['admin', 'super_admin', 'manager'].includes(currentUser?.role);
  const hoverEnabled = isPrivilegedUser && !isMobile;

  // Hide specific emails from calendar view
  const visibleEmployees = useMemo(() =>
    employees.filter(e => {
      const email = (e.email ?? e.workEmail ?? '').toLowerCase().trim();
      return !HIDDEN_EMAILS.includes(email);
    }),
    [employees]
  );

  const deptEmps = useMemo(() =>
    deptFilter === 'All'
      ? visibleEmployees
      : visibleEmployees.filter(e => deptOf(e) === deptFilter),
    [visibleEmployees, deptFilter]
  );

  // A person filtered out by the department picker shouldn't stay selected for export
  useEffect(() => {
    if (exportWho !== 'all' && !deptEmps.some(e => e.id === exportWho)) setExportWho('all');
  }, [deptEmps, exportWho]);

  const filteredEmps = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return deptEmps;
    return deptEmps.filter(e => (e.name ?? '').toLowerCase().includes(q));
  }, [deptEmps, query]);

  const depts = useMemo(() =>
    ['All', ...Array.from(new Set(visibleEmployees.map(deptOf).filter(Boolean))).sort((a, b) => a.localeCompare(b))],
    [visibleEmployees]
  );

  // If the selected department disappears (e.g. renamed), fall back to All
  useEffect(() => {
    if (deptFilter !== 'All' && !depts.includes(deptFilter)) setDeptFilter('All');
  }, [depts, deptFilter]);

  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    let rafId = null;
    let scrolledOnce = false;

    function animateTo(target) {
      const start    = el.scrollLeft;
      const distance = target - start;
      if (Math.abs(distance) < 1) { el.scrollLeft = target; return; }
      const duration = 500;
      let startTime  = null;
      function easeInOutCubic(t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      }
      function step(timestamp) {
        if (!startTime) startTime = timestamp;
        const elapsed  = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        el.scrollLeft  = start + distance * easeInOutCubic(progress);
        if (progress < 1) rafId = requestAnimationFrame(step);
      }
      rafId = requestAnimationFrame(step);
    }

    function performScroll() {
      const today = new Date();
      const isCurrentMonth =
        today.getMonth() === viewDate.getMonth() &&
        today.getFullYear() === viewDate.getFullYear();
      const visibleWidth = el.clientWidth;
      // Container has no laid-out width yet (e.g. still hidden behind an
      // inactive tab) — bail out and let the ResizeObserver below catch it
      // the moment it actually becomes visible, instead of scrolling to 0.
      if (visibleWidth === 0) return;
      // Columns aren't all the same width any more (weekends are narrower),
      // so measure where today's header cell actually sits and centre it in
      // the area to the right of the sticky name column.
      let target = 0;
      const todayCell = isCurrentMonth ? el.querySelector('[data-today="true"]') : null;
      if (todayCell) {
        const elRect     = el.getBoundingClientRect();
        const cellRect   = todayCell.getBoundingClientRect();
        const cellCentre = el.scrollLeft + (cellRect.left - elRect.left) + cellRect.width / 2;
        const viewCentre = LEFT_COL + (visibleWidth - LEFT_COL) / 2;
        target = Math.max(0, cellCentre - viewCentre);
      }
      animateTo(target);
      scrolledOnce = true;
    }

    rafId = requestAnimationFrame(performScroll);

    // Catch the case where clicking into the Calendar page mounts
    // (or reveals) this component before it has a real width — once the
    // container's size settles to something visible, scroll to today then.
    const resizeObserver = new ResizeObserver(() => {
      if (!scrolledOnce && el.clientWidth > 0) {
        performScroll();
      }
    });
    resizeObserver.observe(el);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [viewDate, CELL_MIN, LEFT_COL]);

  // Load the viewed year's leave types, holidays, requests and everyone's
  // allowance / schedule / profile. Re-runs when the YEAR changes (not every
  // month), and ignores a response that arrives after the user has already
  // moved on to another year — that race used to overwrite the balances
  // with the wrong year's allowance.
  useEffect(() => {
    if (!employees.length) return undefined;
    let cancelled = false;
    const year = viewYear;
    async function fetchData() {
      try {
        // Everyone's balance in one call (admins). Anyone missing from it —
        // e.g. colleagues when a member is signed in — falls back to their
        // own allowance call below.
        const bulkRes = await apiService.getLeaveBalances({ year }).catch(() => null);
        const bulk = bulkRes?.data && typeof bulkRes.data === 'object' ? bulkRes.data : {};
        const fromBulk = (empId) => {
          const b = bulk[empId]?.balance;
          if (!b) return null;
          return {
            balance:               b,
            policy:                bulk[empId].policy,
            displayUnit:           b.unit,
            includePublicHolidays: b.observesPublicHolidays !== false && !b.deductPublicHolidays,
            observesPublicHolidays: b.observesPublicHolidays !== false,
            noBalanceTracking:     !!b.noBalanceTracking,
            hoursPerDay:           b.hoursPerDay,
            holidayLocationId:     bulk[empId].holidayLocation?.id ?? null,
            holidayLocationIsDefault: bulk[empId].holidayLocation ? bulk[empId].holidayLocation.isDefault : null,
          };
        };
        const [typesRes, holidaysRes, requestsRes, ...empResults] = await Promise.all([
          apiService.getLeaveTypes(),
          apiService.getPublicHolidays(year).catch(() => null),
          // Scope requests to the viewed year so the payload stays small
          // and previous years never bleed into this year's balance
          apiService.getLeaveRequests({
            limit: 1000,
            scope: 'all',
            from:  `${year}-01-01`,
            to:    `${year}-12-31`,
          }).catch(() => null),
          ...employees.map(emp =>
            Promise.all([
              fromBulk(emp.id) ?? apiService.getEmployeeAllowance(emp.id, year).catch(() => null),
              apiService.getEmployeeSchedules(emp.id).catch(() => null),
              apiService.getEmployeeProfile(emp.id).catch(() => null),
            ]).then(([allowance, schedules, profile]) => ({
              empId:     emp.id,
              allowance: unwrap(allowance),
              schedules: normaliseSchedules(schedules),
              profile:   unwrap(profile),
            }))
          ),
        ]);
        if (cancelled) return;
        const phData = unwrap(holidaysRes) ?? [];
        setPublicHolidays(Array.isArray(phData) ? phData : []);
        const types = (typesRes?.data || []).sort((a, b) => {
          if (a.affectsBalance && !b.affectsBalance) return -1;
          if (!a.affectsBalance && b.affectsBalance) return  1;
          if (a.name === 'Holiday') return -1;
          if (b.name === 'Holiday') return  1;
          return 0;
        });
        setLeaveColors(buildLeaveColors(types));
        const fetchedReqs = requestsRes?.data?.requests ?? requestsRes?.data ?? requestsRes ?? [];
        setCalendarRequests(Array.isArray(fetchedReqs) ? fetchedReqs : []);
        const allowMap = {}, schedMap = {}, profMap = {};
        empResults.forEach(({ empId, allowance, schedules, profile }) => {
          if (allowance && typeof allowance === 'object') allowMap[empId] = allowance;
          if (schedules?.length) schedMap[empId] = schedules;
          if (profile)           profMap[empId]  = profile;
        });
        setAllowances(allowMap);
        setSchedules(schedMap);
        setProfiles(profMap);
        setLoadedYear(year);
      } catch (err) {
        console.error('Calendar fetch error:', err);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [employees, viewYear]); // eslint-disable-line

  function handleRequestAdded(newReq, error) {
    if (error) {
      const message = error?.response?.data?.message;
      const code    = error?.response?.data?.code;
      const isChristmas = newReq?.type === 'Christmas' || newReq?.leaveType?.name === 'Christmas';
      const isDeptConflict = code === 'DEPT_CONFLICT' || message?.includes('colleague');
      if (isDeptConflict && isChristmas) return;
      if (isDeptConflict) { alert('Department conflict:\n' + message); return; }
      alert('Could not submit request: ' + (message ?? 'Unknown error'));
      return;
    }
    if (!newReq) return;
    setCalendarRequests(prev => {
      const exists = prev.some(r => r.id === newReq.id);
      return exists ? prev : [newReq, ...prev];
    });
  }

  async function handleDeleteFromPopup() {
    if (!selectedLeave) return;
    if (deletingId === selectedLeave.leave.id) return;
    const { leave, emp } = selectedLeave;
    setDeletingId(leave.id);
    setSelectedLeave(null);
    try {
      await apiService.deleteLeaveRequest(leave.id);
      setCalendarRequests(prev => prev.filter(r => r.id !== leave.id));
      toast.success('Request deleted');
    } catch (err) {
      setSelectedLeave({ leave, emp });
      toast.error(err?.response?.data?.message || 'Failed to delete request.');
    } finally {
      setDeletingId(null);
    }
  }

  const leaveMap = useMemo(() => {
    const map = {};
    filteredEmps.forEach(emp => {
      const empReqs = calendarRequests.filter(r => (r.empId ?? r.userId) === emp.id);
      map[emp.id] = buildDayLeaveMap(empReqs, days);
    });
    return map;
  }, [filteredEmps, calendarRequests, days]);

  // Close the export / department / month menus on outside click or Escape
  useEffect(() => {
    if (!showExportMenu && !showDeptMenu && !showMonthMenu) return undefined;
    const onDown = e => {
      if (showExportMenu && exportMenuRef.current && !exportMenuRef.current.contains(e.target)) setShowExportMenu(false);
      if (showDeptMenu && deptMenuRef.current && !deptMenuRef.current.contains(e.target)) setShowDeptMenu(false);
      if (showMonthMenu && monthMenuRef.current && !monthMenuRef.current.contains(e.target)) setShowMonthMenu(false);
    };
    const onKey = e => { if (e.key === 'Escape') { setShowExportMenu(false); setShowDeptMenu(false); setShowMonthMenu(false); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showExportMenu, showDeptMenu, showMonthMenu]);

  // Keyboard shortcuts: ← / → change month, T today, N new request, / search.
  // Ignored while typing, while a popup or form is open, and while a hover
  // card is open (it uses ← / → to page its own months).
  useEffect(() => {
    function onKey(e) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      const typing = t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
      if (typing || showForm || prefill || selectedLeave || hoveredEmp) return;
      if (e.key === 'ArrowLeft')  { e.preventDefault(); goToPrevMonth(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goToNextMonth(); }
      else if (e.key === 't' || e.key === 'T') { goToToday(); }
      else if (e.key === 'n' || e.key === 'N') { e.preventDefault(); setShowForm(true); setPrefill(null); }
      else if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // re-bound each render so it always sees the latest state

  // ─── Export approved and pending leave. Everyone in the selected
  // department (one row per request, or a one-row-per-person summary), or a
  // single person with their own summary block. range: 'month' = the month in
  // view, 'year' = January to December of the year in view (the calendar
  // already loads the whole year, so no extra request is needed). ───
  function handleExport(range) {
    setShowExportMenu(false);
    if (!balancesReady) { toast.error('Still loading leave data. Try again in a moment.'); return; }
    const period = exportPeriod(range, viewDate);

    if (exportWho !== 'all') {
      const emp = visibleEmployees.find(e => e.id === exportWho);
      if (!emp) { toast.error('Pick a team member to export.'); return; }
      const stats = computeEmployeeLeaveStats(emp, calendarRequests, allowancesMap, schedulesMap, leaveColors, viewYear, publicHolidays);
      const count = exportPersonLeave({
        emp, requests: calendarRequests, empScheds: schedulesMap[emp.id] ?? [],
        stats, leaveColors, range, monthDate: viewDate,
      });
      toast.success(count
        ? `Downloaded ${emp.name?.split(' ')[0] ?? 'their'}'s leave for ${period.label} (${count} ${count === 1 ? 'entry' : 'entries'}).`
        : `Downloaded ${emp.name?.split(' ')[0] ?? 'their'}'s file for ${period.label}. No leave booked.`);
      return;
    }

    const deptName = deptFilter === 'All' ? null : deptFilter;
    if (exportKind === 'summary') {
      const count = exportTeamSummary({
        emps: deptEmps, requests: calendarRequests, schedulesMap, allowancesMap, leaveColors, publicHolidays,
        range, monthDate: viewDate, deptName,
      });
      toast.success(`Downloaded a summary for ${count} ${count === 1 ? 'person' : 'people'} for ${period.label}.`);
      return;
    }

    const count = exportTeamLeave({
      emps: deptEmps, requests: calendarRequests, schedulesMap, allowancesMap, publicHolidays,
      range, monthDate: viewDate, deptName,
    });
    if (!count) { toast.error(`No leave to export for ${period.label}.`); return; }
    toast.success(`Exported ${count} leave ${count === 1 ? 'entry' : 'entries'} for ${period.label}.`);
  }

  // Download straight from a person's hover card (it may be browsing another month or year)
  function handlePersonDownload(emp, range, monthDate, reqs, stats) {
    const period = exportPeriod(range, monthDate);
    const count = exportPersonLeave({
      emp, requests: reqs, empScheds: schedulesMap[emp.id] ?? [], stats, leaveColors, range, monthDate,
    });
    toast.success(count
      ? `Downloaded ${emp.name?.split(' ')[0] ?? 'their'}'s leave for ${period.label}.`
      : `No leave booked for ${period.label}. The file only has the summary.`);
  }

  function handleDayClick(day, emp) {
    if (isWeekend(day)) return;
    const allowance = allowancesMap[emp.id];
    const ph        = getPublicHoliday(holidaysForEmp(publicHolidays, allowance, emp.id), day);
    if (ph && empObservesHolidays(allowance)) return;
    const key     = day.toISOString();
    const entries = leaveMap[emp.id]?.[key] ?? [];
    if (entries.length > 0) {
      setSelectedLeave({ leave: entries[0].leave, emp, allEntries: entries });
      return;
    }
    const isAdmin = ['admin', 'super_admin'].includes(currentUser?.role);
    const isMgr   = currentUser?.role === 'manager';
    const isOwn   = emp.id === currentUser?.id;
    if (!isAdmin && !isMgr && !isOwn) return;
    const dateStr    = format(day, 'yyyy-MM-dd');
    setPrefill({ from: dateStr, to: dateStr, empId: emp.id, empName: emp.name });
  }

  function goToPrevMonth() {
    setSlideDir('right');
    setAnimKey(k => k + 1);
    setViewDate(d => subMonths(d, 1));
  }

  function goToNextMonth() {
    setSlideDir('left');
    setAnimKey(k => k + 1);
    setViewDate(d => addMonths(d, 1));
  }

  // Jump straight back to the current month and re-center the
  // horizontal scroll on today (reuses the existing scroll-to-today effect,
  // since it re-fires whenever `viewDate` changes)
  function goToToday() {
    setSlideDir(null);
    setViewDate(startOfMonth(new Date()));
  }

  // Jump straight to any month from the title menu
  function openMonthMenu() {
    setPickYear(viewDate.getFullYear());
    setShowMonthMenu(v => !v);
  }
  function jumpToMonth(year, month) {
    const target = new Date(year, month, 1);
    setSlideDir(target > viewDate ? 'left' : target < viewDate ? 'right' : null);
    setAnimKey(k => k + 1);
    setViewDate(target);
    setShowMonthMenu(false);
  }

  // A bar fades when the key or the pending chip is focusing on something else
  const isDimmed = leave =>
    (!!focusType && leave.type !== focusType) ||
    (statusFocus === 'pending' && leave.status !== 'pending');

  // Hover-card show/hide with small delays so moving the cursor
  // from the row to the card itself doesn't cause it to flicker closed
  function scheduleShowHover(emp, targetEl) {
    if (!hoverEnabled) return;
    clearTimeout(hoverHideTimer.current);
    clearTimeout(hoverShowTimer.current);
    hoverShowTimer.current = setTimeout(() => {
      const rect = targetEl.getBoundingClientRect();
      const CARD_W = 310, CARD_H = 520, GAP = 10, MARGIN = 12;
      let left = rect.right + GAP;
      if (left + CARD_W > window.innerWidth - MARGIN) left = Math.max(MARGIN, rect.left - CARD_W - GAP);
      let top = rect.top - 8;
      if (top + CARD_H > window.innerHeight - MARGIN) top = Math.max(MARGIN, window.innerHeight - CARD_H - MARGIN);
      setHoveredEmp({ emp, top, left });
    }, 280);
  }
  function scheduleHideHover() {
    clearTimeout(hoverShowTimer.current);
    clearTimeout(hoverHideTimer.current);
    hoverHideTimer.current = setTimeout(() => setHoveredEmp(null), 160);
  }
  function cancelHideHover() {
    clearTimeout(hoverHideTimer.current);
  }

  // ─── Layout data for the grid ───
  const monthStartStr = format(days[0], 'yyyy-MM-dd');
  const monthEndStr   = format(days[days.length - 1], 'yyyy-MM-dd');

  // Per-day facts worked out once, not once per person
  const dayInfo = days.map(day => {
    const isWk = isWeekend(day);
    return {
      day,
      key:     day.toISOString(),
      isWk,
      isToday: isSameDay(day, new Date()),
      ph:      getPublicHoliday(publicHolidays, day),
      flex:    colFlex(isWk),
      minW:    Math.round(CELL_MIN * colFlex(isWk)),
    };
  });
  const gridMinWidth = LEFT_COL + dayInfo.reduce((sum, d) => sum + d.minW, 0);

  // Each person's own public holidays (their holiday location, minus exclusions)
  const empHolidaysById = Object.fromEntries(
    employees.map(emp => [emp.id, holidaysForEmp(publicHolidays, allowancesMap[emp.id], emp.id)])
  );

  // Rows grouped by department when every department is showing
  const groups = (() => {
    if (deptFilter !== 'All') return [{ dept: null, emps: filteredEmps }];
    const byDept = new Map();
    filteredEmps.forEach(emp => {
      const d = deptOf(emp) || NO_DEPT;
      if (!byDept.has(d)) byDept.set(d, []);
      byDept.get(d).push(emp);
    });
    if (byDept.size <= 1) return [{ dept: null, emps: filteredEmps }];
    const named = [...byDept.entries()].filter(([d]) => d !== NO_DEPT).sort((a, b) => a[0].localeCompare(b[0]));
    const rest  = byDept.has(NO_DEPT) ? [[NO_DEPT, byDept.get(NO_DEPT)]] : [];
    return [...named, ...rest].map(([dept, emps]) => ({ dept, emps }));
  })();

  // Leave on show this month, for the toolbar counts and the legend
  const visibleEmpIds = new Set(filteredEmps.map(e => e.id));
  const monthRequests = calendarRequests.filter(r => {
    if (r.status === 'rejected' || r.status === 'cancelled') return false;
    if (!visibleEmpIds.has(r.empId ?? r.userId)) return false;
    const from = (r.from ?? '').slice(0, 10);
    const to   = (r.to ?? r.from ?? '').slice(0, 10);
    return from <= monthEndStr && to >= monthStartStr;
  });
  // Every leave type the company has, with how many are on the grid this month
  const legendTypes = Object.entries(leaveColors).map(([type, cfg]) => {
    const ofType = monthRequests.filter(r => r.type === type);
    return {
      type, cfg,
      approved: ofType.filter(r => r.status === 'approved').length,
      inUse:    ofType.length,
    };
  });
  // By default the key only lists the types actually on the grid this month
  const usedTypes       = legendTypes.filter(t => t.inUse > 0 || t.type === focusType);
  const shownTypes      = showAllTypes ? legendTypes : usedTypes;
  const hiddenTypeCount = legendTypes.length - usedTypes.length;
  const approvedInView = monthRequests.filter(r => r.status === 'approved').length;
  const pendingInView  = monthRequests.filter(r => r.status === 'pending').length;

  // ─── Summary cards ───
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const isViewingThisMonth = todayStr >= monthStartStr && todayStr <= monthEndStr;
  const outToday = (() => {
    const seen = new Map();
    calendarRequests.forEach(r => {
      if (r.status !== 'approved') return;
      const id = r.empId ?? r.userId;
      if (!visibleEmpIds.has(id) || seen.has(id)) return;
      const from = (r.from ?? '').slice(0, 10);
      const to   = (r.to ?? r.from ?? '').slice(0, 10);
      if (from <= todayStr && to >= todayStr) {
        const emp = filteredEmps.find(e => e.id === id);
        if (emp) seen.set(id, { emp, type: r.type });
      }
    });
    return [...seen.values()];
  })();
  const nextBankHoliday = publicHolidays
    .map(h => ({ ...h, d: String(h.date ?? '').slice(0, 10) }))
    .filter(h => h.d >= (isViewingThisMonth ? todayStr : monthStartStr))
    .sort((a, b) => a.d.localeCompare(b.d))[0] ?? null;
  const offToday = (emps) => {
    const ids = new Set(outToday.map(o => o.emp.id));
    return emps.filter(e => ids.has(e.id)).length;
  };
  const toggleGroup = (dept) => setCollapsed(prev => {
    const next = new Set(prev);
    next.has(dept) ? next.delete(dept) : next.add(dept);
    return next;
  });

  // Who's away each day, for the count under each date in the header.
  // Working remotely, meetings and training don't count as away.
  const isAwayType = type => /not working/i.test(type ?? '') || !/remote|working|meeting|training/i.test(type ?? '');
  const awayByDay = dayInfo.map(({ key, isWk }) => {
    if (isWk) return [];
    const names = [];
    filteredEmps.forEach(emp => {
      const entries = leaveMap[emp.id]?.[key] ?? [];
      if (entries.some(e => isAwayType(e.leave.type))) names.push(emp.name);
    });
    return names;
  });
  // Flag a day once a large share of the people shown are away
  const crowdedAt = Math.max(2, Math.ceil(filteredEmps.length * 0.3));

  // Rows actually drawn (collapsed departments don't count) and their headers
  const visibleRowCount  = groups.reduce((n, g) => n + (collapsed.has(g.dept) ? 0 : g.emps.length), 0);
  const groupHeaderCount = groups.filter(g => g.dept).length;

  // Fit the grid card to the window (desktop and tablet; on phones the page
  // scrolls instead). In Fit mode, also pick the row height that shows the
  // whole team without scrolling, between FIT_MIN and FIT_MAX pixels.
  useEffect(() => {
    const FIT_MIN = 22, FIT_MAX = 50;
    function measure() {
      if (isMobile) { setGridMaxH(null); return; }
      const card = gridCardRef.current;
      if (!card) return;
      const top    = card.getBoundingClientRect().top + window.scrollY;
      const cardH  = Math.max(360, Math.round(window.innerHeight - top - 16));
      setGridMaxH(prev => (prev === cardH ? prev : cardH));
      if (!fitMode) return;
      const keybarH   = keybarRef.current?.offsetHeight ?? 0;
      // room for the sideways scrollbar, only when the month is wider than the card
      const horizBar  = card.clientWidth < gridMinWidth ? 12 : 0;
      const available = cardH - keybarH - HEADER_H - groupHeaderCount * GROUP_H - horizBar - 2;
      const rows      = Math.max(1, visibleRowCount);
      const next      = Math.max(FIT_MIN, Math.min(FIT_MAX, Math.floor(available / rows)));
      setFitRowH(prev => (prev === next ? prev : next));
    }
    measure();
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); };
  }, [isMobile, fitMode, windowWidth, visibleRowCount, groupHeaderCount, HEADER_H, GROUP_H, gridMinWidth, shownTypes.length, focusType, statusFocus, publicHolidays.length]);

  // Days until the next bank holiday, for the summary line
  const bankHolidayIn = nextBankHoliday
    ? Math.round((parseISO(nextBankHoliday.d) - parseISO(todayStr)) / 86400000)
    : null;

  const deptLabel   = d => (d === 'All' ? 'All departments' : d);
  const useDeptMenu = isMobile || isTablet || depts.length > 4;

  function renderRow(emp, isLastRow) {
    const empScheds   = schedulesMap[emp.id] ?? [];
    const empProfile  = profilesMap[emp.id];
    const observesPH  = empObservesHolidays(allowancesMap[emp.id]);
    const empHolidays = empHolidaysById[emp.id] ?? [];
    const isOwnRow    = emp.id === currentUser?.id;
    const isRowHovered = hoveredRowId === emp.id;
    const rowBg       = isRowHovered ? 'var(--hover)' : 'var(--surface)';
    // The sticky name cell must stay opaque: --hover / --weekend are translucent in dark
    // mode, so leave bars scrolled underneath would show through. Layer the tint over a
    // solid --surface instead.
    const nameBg      = isRowHovered
      ? 'linear-gradient(var(--hover), var(--hover)), var(--surface)'
      : 'var(--surface)';
    const canSeeBalance = (isOwnRow || isPrivilegedUser) && balancesReady;
    const canClickForNew = isPrivilegedUser || isOwnRow;
    // One stats calculation per row, shared by the badge and the meter.
    // viewYear so the balance only counts this year's requests.
    const stats = canSeeBalance
      ? computeEmployeeLeaveStats(emp, calendarRequests, allowancesMap, schedulesMap, leaveColors, viewYear, publicHolidays)
      : null;
    const { remainingLabel, pendingLabel } = balanceLabels(stats);
    const overdrawn = !!stats && !stats.noBalanceTracking &&
      (stats.displayUnit === 'hours' ? stats.remainingHours : stats.remainingDays) < 0;
    const balanceColor = overdrawn ? 'var(--bad-text)' : 'var(--accent)';
    // How much of the year's allowance is left, for the thin meter under the name
    const meter = (() => {
      if (!stats || isMobile || compact || !remainingLabel) return null;
      if (stats.noBalanceTracking || !stats.totalHours) return null;
      const usedFrac    = Math.min(1, Math.max(0, stats.used.hours / stats.totalHours));
      const pendingFrac = Math.min(1 - usedFrac, stats.pending.hours / stats.totalHours);
      return { usedFrac, pendingFrac };
    })();

    // What is drawn on each day, so neighbouring days can join into one bar
    const cells = dayInfo.map(info => {
      // Their own location's holiday on this day (another location's doesn't count)
      const ph = info.ph ? getPublicHoliday(empHolidays, info.day) : null;
      const isBankHoliday = !!ph && observesPH;
      const isOff   = info.isWk || isBankHoliday;
      const entries = isOff ? [] : (leaveMap[emp.id]?.[info.key] ?? []);
      return { ...info, ph, isBankHoliday, isOff, entries };
    });
    const hasFull = (cell, leave) => !!cell && cell.entries.some(e => e.leave === leave && !isHalfDayLeave(e.leave));

    return (
      <div
        key={emp.id}
        onMouseEnter={() => setHoveredRowId(emp.id)}
        onMouseLeave={() => setHoveredRowId(prev => (prev === emp.id ? null : prev))}
        style={{ display: 'flex', height: ROW_HEIGHT, boxSizing: 'border-box', borderBottom: isLastRow ? 'none' : '1px solid var(--border-soft)', background: rowBg, transition: 'background 0.12s' }}
      >
        <div
          className={hoverEnabled ? 'cal-emp-name' : undefined}
          onMouseEnter={hoverEnabled ? (e) => scheduleShowHover(emp, e.currentTarget) : undefined}
          onMouseLeave={hoverEnabled ? scheduleHideHover : undefined}
          style={{
            width: LEFT_COL, minWidth: LEFT_COL, flexShrink: 0, boxSizing: 'border-box', display: 'flex', alignItems: 'center',
            gap: compact ? 11 : 14, padding: isMobile ? '0 12px 0 16px' : tinyRows ? '0 16px 0 20px' : '0 20px 0 24px',
            borderRight: '1px solid var(--border)', background: nameBg, position: 'sticky', left: 0, zIndex: 20,
            cursor: hoverEnabled ? 'pointer' : 'default', transition: 'background 0.12s',
          }}
        >
          {showAvatar && <Avatar employee={emp} size={isTablet || compact ? 'sm' : 'md'} style={{ flexShrink: 0 }} />}
          <div style={{ minWidth: 0, flex: 1 }}>
            <p title={emp.name} style={{ fontSize: microRows ? 12 : tinyRows ? 12.5 : compact ? 13.5 : 14, fontWeight: 600, color: 'var(--text)', margin: 0, lineHeight: 1.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isMobile ? emp.name.split(' ')[0] : emp.name}
              {isOwnRow && !isMobile && !microRows && <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>(you)</span>}
              {!showSubline && remainingLabel && (
                <span style={{ marginLeft: 8, fontSize: 11.5, fontWeight: 600, color: balanceColor }}>{remainingLabel}</span>
              )}
            </p>
            {showSubline && (remainingLabel || pendingLabel) && (
              <p style={{ margin: compact ? '2px 0 0' : '4px 0 0', fontSize: compact ? 11.5 : 12, lineHeight: 1.3, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {remainingLabel && <span><span style={{ fontWeight: 600, color: balanceColor }}>{remainingLabel}</span> left</span>}
                {remainingLabel && pendingLabel && !isMobile && <span style={{ color: 'var(--field-border)', margin: '0 8px' }}>·</span>}
                {pendingLabel && !isMobile && <span style={{ color: 'var(--warn-text)' }}><span style={{ fontWeight: 600 }}>{pendingLabel}</span> pending</span>}
              </p>
            )}
            {meter && (
              <div aria-hidden="true" style={{ marginTop: 7, height: 3, borderRadius: 2, background: 'var(--tint)', overflow: 'hidden', display: 'flex', maxWidth: 140 }}>
                <span style={{ width: (meter.usedFrac * 100) + '%', background: 'var(--accent)', opacity: 0.85 }} />
                <span style={{ width: (meter.pendingFrac * 100) + '%', background: '#F5A300', opacity: 0.7 }} />
              </div>
            )}
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', position: 'relative' }}>
          {cells.map((cell, i) => {
            const { day, key, isWk, isToday, isBankHoliday, isOff, entries, flex, minW } = cell;
            const isNonWork   = !isOff && isNonWorkingDay(empScheds, day);
            const weekStart   = i > 0 && day.getDay() === 1;
            const isBday      = isBirthday(empProfile, day);
            const isClickable = !isOff && (entries.length > 0 || canClickForNew);

            let cellBg = 'transparent';
            if (isToday)       cellBg = 'var(--today-cell)';
            if (isWk)          cellBg = 'var(--weekend)';
            if (isBankHoliday) cellBg = 'var(--holiday-bg)';
            // Bank holidays get their own marigold stripes so they read at a glance
            const hatch = isBankHoliday
              ? 'repeating-linear-gradient(135deg, var(--holiday-stripe) 0 2px, transparent 2px 8px)'
              : isNonWork
                ? 'repeating-linear-gradient(135deg, rgba(140,135,121,0.16) 0 1.5px, transparent 1.5px 7px)'
                : undefined;

            return (
              <div
                key={key}
                className={isClickable && entries.length === 0 ? 'cal-cell cal-cell-free' : 'cal-cell'}
                title={isBankHoliday ? cell.ph.name : isNonWork ? 'Non-working day' : undefined}
                onClick={() => !isOff && handleDayClick(day, emp)}
                style={{
                  flex, minWidth: minW, position: 'relative', boxSizing: 'border-box',
                  borderLeft: isBankHoliday ? '1px solid var(--holiday-edge)' : weekStart ? '1px solid var(--border-soft)' : 'none',
                  borderRight: isBankHoliday ? '1px solid var(--holiday-edge)' : 'none',
                  backgroundColor: cellBg, backgroundImage: hatch, cursor: isClickable ? 'pointer' : 'default',
                }}
              >
                {isBankHoliday && entries.length === 0 && !isBday && (
                  <div aria-hidden="true" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <Flag size={13} strokeWidth={2.2} style={{ color: 'var(--holiday-ink)', opacity: 0.8 }} />
                  </div>
                )}
                {isBday && entries.length === 0 && (
                  <div title={emp.name + "'s birthday"} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 8 }}>
                    <Cake size={tinyRows ? 12 : 15} color="#c98a00" strokeWidth={2} />
                  </div>
                )}
                {entries.map((e, n) => {
                  const half = isHalfDayLeave(e.leave) ? (getHalfDayPeriod(e.leave) ?? 'AM') : null;
                  if (half) {
                    return (
                      <LeaveBar key={n} leave={e.leave} leaveColors={leaveColors} barTop={BAR_TOP} barHeight={BAR_H}
                        roundLeft roundRight half={half} onActivate={() => handleDayClick(day, emp)}
                        dimmed={isDimmed(e.leave)} />
                    );
                  }
                  const joinsPrev = hasFull(cells[i - 1], e.leave);
                  const joinsNext = hasFull(cells[i + 1], e.leave);
                  // Leave that started before / carries on after this month keeps a flat edge
                  const flatLeft  = i === 0 && (e.leave.from ?? '').slice(0, 10) < monthStartStr;
                  const flatRight = i === cells.length - 1 && (e.leave.to ?? '').slice(0, 10) > monthEndStr;
                  let runLen = 1;
                  if (!joinsPrev) { while (hasFull(cells[i + runLen], e.leave)) runLen += 1; }
                  return (
                    <LeaveBar key={n} leave={e.leave} leaveColors={leaveColors} barTop={BAR_TOP} barHeight={BAR_H}
                      roundLeft={!joinsPrev && !flatLeft} roundRight={!joinsNext && !flatRight}
                      flatLeft={flatLeft} flatRight={flatRight}
                      showIcon={!joinsPrev}
                      label={!joinsPrev && runLen >= 3 ? e.leave.type : null}
                      labelSpan={runLen}
                      onActivate={!joinsPrev ? () => handleDayClick(day, emp) : undefined}
                      dimmed={isDimmed(e.leave)} />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 10 }}>

      <style>{`
        @keyframes slideIn-left {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideIn-right {
          from { opacity: 0; transform: translateX(-24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes popupIn {
          from { opacity: 0; transform: translate(-50%, -48%) scale(0.96); }
          to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes hoverCardIn {
          from { opacity: 0; transform: translateY(4px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .cal-btn {
          height: 40px; padding: 0 16px; display: inline-flex; align-items: center; gap: 7px;
          border: 1px solid var(--border); border-radius: 10px; background: var(--surface); color: var(--text-2);
          font-size: 13px; font-weight: 600; white-space: nowrap; cursor: pointer; flex-shrink: 0;
          transition: background 0.15s, border-color 0.15s;
        }
        .cal-btn:hover { background: var(--border-soft); border-color: var(--field-border); }
        .cal-btn-icon { width: 40px; padding: 0; justify-content: center; }
        .cal-btn-primary { background: #FFC81E; border-color: #FFC81E; color: #1C1A14; }
        .cal-btn-primary:hover { background: #F5A300; border-color: #F5A300; }

        .cal-seg { display: flex; gap: 2px; padding: 3px; background: var(--border-soft); border-radius: 10px; }
        .cal-seg button {
          height: 32px; padding: 0 14px; border: none; border-radius: 8px; background: transparent;
          color: var(--text-2); font-size: 13px; font-weight: 500; white-space: nowrap; cursor: pointer;
          transition: background 0.15s, color 0.15s;
        }
        .cal-seg button:hover { color: var(--text); }
        .cal-seg button[aria-pressed="true"] { background: var(--surface); color: var(--accent); font-weight: 600; box-shadow: 0 1px 2px rgba(28,26,20,0.10); }

        .cal-menu {
          position: absolute; top: calc(100% + 6px); right: 0; z-index: 100; min-width: 200px; padding: 4px;
          background: var(--surface); border: 1px solid var(--border); border-radius: 12px; box-shadow: 0 12px 32px rgba(28,26,20,0.12);
        }
        .cal-menu-item {
          display: block; width: 100%; padding: 8px 10px; text-align: left; border: none; border-radius: 8px;
          background: transparent; color: var(--text); font-size: 13px; cursor: pointer;
        }
        .cal-menu-item:hover { background: var(--border-soft); }
        .cal-hc-dl { width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center; border: 1px solid var(--border); border-radius: 8px; background: var(--surface); color: var(--text-2); cursor: pointer; }
        .cal-hc-dl:hover:not(:disabled) { color: var(--accent); border-color: var(--accent); }
        .cal-hc-dl:disabled { opacity: 0.5; cursor: wait; }
        .cal-export { min-width: 280px; width: 300px; max-width: calc(100vw - 32px); padding: 12px; box-sizing: border-box; }
        .cal-export-label { margin: 0 2px 6px; font-size: 11px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.06em; }
        .cal-export-select { width: 100%; height: 38px; padding: 0 10px; border: 1px solid var(--field-border); border-radius: 9px; background: var(--surface); color: var(--text); font-size: 13px; cursor: pointer; }
        .cal-export-select:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
        .cal-export-kind { display: flex; flex-direction: column; gap: 6px; margin-top: 8px; }
        .cal-export-kind label { display: flex; align-items: flex-start; gap: 8px; padding: 8px 10px; border: 1px solid var(--border); border-radius: 9px; font-size: 13px; color: var(--text); cursor: pointer; }
        .cal-export-kind label[data-checked="true"] { border-color: var(--accent); background: var(--tint); }
        .cal-export-kind input { margin: 2px 0 0; accent-color: var(--accent); }
        .cal-export-go { display: flex; align-items: center; gap: 10px; }
        .cal-menu-item[aria-checked="true"] { background: var(--tint); color: var(--accent); font-weight: 600; }

        .cal-search {
          width: 100%; height: 40px; padding: 0 12px 0 36px; border: 1px solid var(--border); border-radius: 10px;
          background: var(--surface-2); color: var(--text); font-size: 13px;
        }
        .cal-search:focus { background: var(--surface); }
        .cal-search::placeholder { color: var(--text-muted); }

        .cal-emp-name:hover { background: linear-gradient(var(--weekend), var(--weekend)), var(--surface) !important; }
        .cal-cell { transition: background-color 0.12s; }
        .cal-cell-free:hover { background-color: var(--hover) !important; }

        .cal-bar { box-shadow: 0 1px 1px rgba(28,26,20,0.06); transition: filter 0.12s, opacity 0.15s, transform 0.12s; }
        .cal-bar-pending { box-shadow: none; }
        .cal-bar:hover { filter: brightness(0.95); transform: translateY(-1px); }
        .cal-bar:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; z-index: 12 !important; }

        .cal-strip { display: flex; flex-wrap: wrap; gap: 8px; }
        .cal-chip { display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 12px; border: 1px solid var(--border); border-radius: 16px; background: var(--surface); color: var(--text-2); font-size: 12.5px; white-space: nowrap; min-width: 0; }
        .cal-chip svg { color: var(--text-muted); flex-shrink: 0; }
        .cal-chip b { color: var(--text); font-weight: 700; }
        .cal-chip-names { margin-left: 4px; padding-left: 9px; border-left: 1px solid var(--border); color: var(--text-muted); overflow: hidden; text-overflow: ellipsis; max-width: 220px; }
        .cal-chip-warn { background: var(--warn-bg-soft); border-color: var(--warn-border); color: var(--warn-text); }
        .cal-chip-warn b, .cal-chip-warn svg { color: var(--warn-text); }
        .cal-chip-holiday { background: var(--holiday-bg); border-color: var(--holiday-edge); color: var(--holiday-ink); }
        .cal-chip-holiday b, .cal-chip-holiday svg { color: var(--holiday-ink); }
        .cal-chip-holiday .cal-chip-names { border-left-color: var(--holiday-edge); color: var(--holiday-ink); }

        .cal-group-btn { display: flex; align-items: center; gap: 9px; width: 100%; height: 100%; padding: 0; border: none; background: transparent; cursor: pointer; text-align: left; font: inherit; }
        .cal-group-btn:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; border-radius: 6px; }
        .cal-pill { display: inline-flex; align-items: center; height: 20px; padding: 0 8px; border-radius: 10px; font-size: 11.5px; font-weight: 600; white-space: nowrap; flex-shrink: 0; }

        .cal-key { display: inline-flex; align-items: center; gap: 8px; height: 32px; padding: 0 10px 0 6px; border: 1px solid transparent; border-radius: 9px; background: transparent; color: var(--text-2); font-size: 13px; white-space: nowrap; cursor: pointer; transition: background 0.15s, border-color 0.15s, opacity 0.15s; }
        .cal-key:hover { background: var(--surface); border-color: var(--border); }
        .cal-key[aria-pressed="true"] { background: var(--surface); border-color: var(--accent); color: var(--accent); font-weight: 600; }
        .cal-key-dim { opacity: 0.45; }
        .cal-key-more { color: var(--accent); font-weight: 600; padding: 0 10px; }

        .cal-popup-x { position: absolute; top: 14px; right: 14px; width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center; border: none; border-radius: 8px; background: transparent; color: var(--text-muted); cursor: pointer; }
        .cal-popup-x:hover { background: var(--border-soft); color: var(--text); }
        .cal-popup-delete:hover { background: var(--bad-bg) !important; }
        .cal-popup-close:hover { background: var(--border-soft) !important; }

        .cal-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding-top: 2px; }
        .cal-title {
          display: inline-flex; align-items: center; gap: 6px; max-width: 100%; padding: 4px 8px 4px 6px; margin-left: -6px;
          border: none; border-radius: 10px; background: transparent; cursor: pointer; transition: background 0.15s;
        }
        .cal-title:hover, .cal-title[aria-expanded="true"] { background: var(--border-soft); }
        .cal-title:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
        .cal-title h1 { margin: 0; font-size: 26px; font-weight: 700; letter-spacing: -0.02em; color: var(--text); white-space: nowrap; }
        @media (max-width: 639px) { .cal-title h1 { font-size: 20px; } }

        .cal-navgroup { display: inline-flex; align-items: stretch; height: 38px; border: 1px solid var(--border); border-radius: 10px; background: var(--surface); overflow: hidden; flex-shrink: 0; }
        .cal-navgroup button { display: inline-flex; align-items: center; justify-content: center; min-width: 36px; padding: 0 8px; border: none; background: transparent; color: var(--text-2); font-size: 13px; font-weight: 600; cursor: pointer; transition: background 0.15s, color 0.15s; }
        .cal-navgroup button + button { border-left: 1px solid var(--border); }
        .cal-navgroup button:hover:not(:disabled) { background: var(--border-soft); color: var(--text); }
        .cal-navgroup button:disabled { color: var(--text-faint); cursor: default; }
        .cal-navgroup button:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
        .cal-navgroup-today { padding: 0 14px !important; }

        .cal-month-menu { left: -6px; right: auto; width: 252px; padding: 10px; }
        .cal-mini-btn { width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center; border: none; border-radius: 8px; background: transparent; color: var(--text-2); cursor: pointer; }
        .cal-mini-btn:hover { background: var(--border-soft); color: var(--text); }
        .cal-month-cell { height: 38px; border: 1px solid transparent; border-radius: 9px; background: transparent; color: var(--text); font-size: 13px; font-weight: 500; cursor: pointer; transition: background 0.12s; }
        .cal-month-cell:hover { background: var(--border-soft); }
        .cal-month-cell[data-now] { border-color: var(--accent); color: var(--accent); font-weight: 600; }
        .cal-month-cell[aria-current="date"] { background: var(--accent); border-color: var(--accent); color: var(--on-accent); font-weight: 600; }

        .cal-head { flex-wrap: wrap; row-gap: 10px; }
        .cal-search-wrap { position: relative; flex: 0 1 220px; min-width: 150px; }
        @media (max-width: 639px) { .cal-search-wrap { flex: 1 1 100%; } }

        .cal-summary { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 6px; margin: -2px 0 2px; }
        .cal-sum {
          display: inline-flex; align-items: center; gap: 6px; height: 28px; padding: 0 9px; margin-left: -9px;
          border: 1px solid transparent; border-radius: 8px; background: transparent;
          color: var(--text-2); font: inherit; font-size: 13px; white-space: nowrap;
        }
        .cal-sum + .cal-sum { margin-left: 0; }
        .cal-sum + .cal-sum::before { content: ''; width: 3px; height: 3px; border-radius: 50%; background: var(--text-faint); margin: 0 6px 0 -12px; }
        .cal-sum svg { color: var(--text-muted); flex-shrink: 0; }
        .cal-sum b { color: var(--text); font-weight: 700; }
        .cal-sum-muted { color: var(--text-muted); }
        button.cal-sum { cursor: pointer; transition: background 0.15s, border-color 0.15s; }
        button.cal-sum:hover { background: var(--border-soft); }
        button.cal-sum:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
        .cal-sum-warn, .cal-sum-warn b, .cal-sum-warn svg { color: var(--warn-text); }
        button.cal-sum-warn:hover { background: var(--warn-bg-soft); }
        .cal-sum-warn[aria-pressed="true"] { background: var(--warn-bg-soft); border-color: var(--warn-border); }
        .cal-sum-holiday svg, .cal-sum-holiday b { color: var(--holiday-ink); }
        @media (max-width: 639px) {
          .cal-summary { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; margin: -2px -16px 0; padding: 0 16px; }
          .cal-summary::-webkit-scrollbar { display: none; }
          .cal-sum { flex-shrink: 0; }
        }
        .cal-filters { display: flex; align-items: center; gap: 8px; flex-wrap: nowrap; margin-left: auto; }
        @media (max-width: 639px) { .cal-filters { margin-left: 0; width: 100%; flex-wrap: wrap; } }
        .cal-chip-btn { font: inherit; cursor: pointer; transition: background 0.15s, border-color 0.15s, box-shadow 0.15s; }
        .cal-chip-btn:hover:not(:disabled) { border-color: var(--field-border); background: var(--surface-2); }
        .cal-chip-warn.cal-chip-btn:hover:not(:disabled) { background: var(--warn-bg); }
        .cal-chip-btn:disabled { cursor: default; }
        .cal-chip-btn[aria-pressed="true"] { border-color: var(--warn-text); box-shadow: 0 0 0 1px var(--warn-text) inset; }
        .cal-chip-btn:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

        /* Phones: chips and key become single rows you swipe sideways */
        @media (max-width: 639px) {
          .cal-strip, .cal-keybar { flex-wrap: nowrap !important; overflow-x: auto; scrollbar-width: none; -webkit-overflow-scrolling: touch; }
          .cal-strip::-webkit-scrollbar, .cal-keybar::-webkit-scrollbar { display: none; }
          .cal-strip { margin: 0 -16px; padding: 0 16px; width: calc(100% + 32px); box-sizing: border-box; }
          .cal-strip > *, .cal-keybar > * { flex-shrink: 0; }
          .cal-patterns { display: none !important; }
          .cal-controls { gap: 10px; }
        }

        /* How many people are away that day, under the date */
        .cal-away { min-width: 16px; height: 15px; padding: 0 4px; box-sizing: border-box; border-radius: 8px; color: var(--text-faint); font-size: 10.5px; font-weight: 600; line-height: 15px; text-align: center; }
        .cal-away-crowded { background: var(--warn-bg); color: var(--warn-text); font-weight: 700; box-shadow: 0 0 0 1px var(--warn-border) inset; }

        /* A faint plus on empty days you can click to book */
        .cal-cell-free:hover::after { content: '+'; position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: var(--accent); font-size: 17px; font-weight: 500; opacity: 0.55; pointer-events: none; }

        .cal-scroll::-webkit-scrollbar { width: 10px; }
        .cal-scroll { scrollbar-width: thin; scrollbar-color: var(--field-border) transparent; }
        .cal-scroll::-webkit-scrollbar { height: 10px; }
        .cal-scroll::-webkit-scrollbar-track { background: transparent; }
        .cal-scroll::-webkit-scrollbar-thumb { background-color: var(--field-border); border-radius: 8px; border: 2px solid var(--surface); }
        .cal-scroll::-webkit-scrollbar-thumb:hover { background-color: var(--text-faint); }
      `}</style>

      {/* ─── Header: month + navigation on the left; search, filters and the
      main action on the right. One row on desktop so the wall chart gets the
      height. ─── */}
      <div className="cal-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 14, minWidth: 0 }}>
          <div ref={monthMenuRef} style={{ position: 'relative', minWidth: 0, flexShrink: 0 }}>
            <button
              type="button"
              className="cal-title"
              onClick={openMonthMenu}
              aria-haspopup="dialog"
              aria-expanded={showMonthMenu}
              title="Jump to a month"
            >
              <h1>{format(viewDate, isMobile ? 'MMM yyyy' : 'MMMM yyyy')}</h1>
              <ChevronDown size={isMobile ? 16 : 18} style={{ flexShrink: 0, color: 'var(--text-muted)', transform: showMonthMenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
            {showMonthMenu && (
              <div className="cal-menu cal-month-menu" role="dialog" aria-label="Choose a month">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 4px 10px' }}>
                  <button type="button" className="cal-mini-btn" onClick={() => setPickYear(y => y - 1)} aria-label="Previous year"><ChevronLeft size={15} /></button>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{pickYear}</span>
                  <button type="button" className="cal-mini-btn" onClick={() => setPickYear(y => y + 1)} aria-label="Next year"><ChevronRight size={15} /></button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                  {Array.from({ length: 12 }, (_, m) => {
                    const isViewed = pickYear === viewDate.getFullYear() && m === viewDate.getMonth();
                    const isNow    = pickYear === new Date().getFullYear() && m === new Date().getMonth();
                    return (
                      <button
                        key={m}
                        type="button"
                        className="cal-month-cell"
                        aria-current={isViewed ? 'date' : undefined}
                        data-now={isNow || undefined}
                        onClick={() => jumpToMonth(pickYear, m)}
                      >
                        {format(new Date(pickYear, m, 1), 'MMM')}
                      </button>
                    );
                  })}
                </div>
                <button type="button" className="cal-menu-item" onClick={() => { goToToday(); setShowMonthMenu(false); }} style={{ marginTop: 8, textAlign: 'center', color: 'var(--accent)', fontWeight: 600, borderTop: '1px solid var(--border-soft)', borderRadius: '0 0 8px 8px' }}>
                  Back to this month
                </button>
              </div>
            )}
          </div>

          <div className="cal-navgroup" role="group" aria-label="Change month">
            <button onClick={goToPrevMonth} aria-label="Previous month" title="Previous month (←)"><ChevronLeft size={16} /></button>
            <button onClick={goToToday} title="Jump to today (T)" disabled={isViewingThisMonth} className="cal-navgroup-today">Today</button>
            <button onClick={goToNextMonth} aria-label="Next month" title="Next month (→)"><ChevronRight size={16} /></button>
          </div>
        </div>

        <div className="cal-filters">
          <div className="cal-search-wrap">
            <Search size={15} style={{ color: 'var(--text-muted)', position: 'absolute', left: 12, top: 12.5, pointerEvents: 'none' }} />
            <input
              ref={searchRef}
              type="search"
              className="cal-search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') { setQuery(''); e.currentTarget.blur(); } }}
              placeholder={`Search ${deptEmps.length} ${deptEmps.length === 1 ? 'person' : 'people'}`}
              aria-label="Search people"
              title="Search people (/)"
            />
          </div>

          {depts.length > 2 && (useDeptMenu ? (
            <div ref={deptMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
              <button className="cal-btn" onClick={() => setShowDeptMenu(v => !v)} aria-haspopup="menu" aria-expanded={showDeptMenu} style={{ maxWidth: isMobile ? 190 : 240 }}>
                <Users size={15} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{deptLabel(deptFilter)}</span>
                <ChevronDown size={14} style={{ flexShrink: 0, transform: showDeptMenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              {showDeptMenu && (
                <div role="menu" className="cal-menu" style={{ maxHeight: 320, overflowY: 'auto', right: isMobile ? 'auto' : 0, left: isMobile ? 0 : 'auto' }}>
                  {depts.map(d => (
                    <button key={d} role="menuitemradio" aria-checked={deptFilter === d} className="cal-menu-item" onClick={() => { setDeptFilter(d); setShowDeptMenu(false); }}>
                      {deptLabel(d)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="cal-seg" role="group" aria-label="Department">
              {depts.map(d => (
                <button key={d} aria-pressed={deptFilter === d} onClick={() => setDeptFilter(d)}>{d}</button>
              ))}
            </div>
          ))}

          {!isMobile && (
            <div className="cal-seg" role="group" aria-label="Row size">
              <button aria-pressed={density === 'fit'} onClick={() => setDensity('fit')} title="Shrink rows so the whole team fits on screen">Fit</button>
              <button aria-pressed={density === 'compact'} onClick={() => setDensity('compact')} title="Fixed compact rows">Compact</button>
              <button aria-pressed={density === 'comfortable'} onClick={() => setDensity('comfortable')} title="Taller rows">Roomy</button>
            </div>
          )}

          {isPrivilegedUser && (
            <div ref={exportMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
              <button
                className="cal-btn cal-btn-icon"
                onClick={() => setShowExportMenu(v => !v)}
                title="Export leave to CSV"
                aria-label="Export leave to CSV"
                aria-haspopup="menu"
                aria-expanded={showExportMenu}
              >
                <Download size={15} />
              </button>
              {showExportMenu && (() => {
                const people = [...deptEmps].sort((x, y) => (x.name ?? '').localeCompare(y.name ?? ''));
                const person = exportWho === 'all' ? null : visibleEmployees.find(e => e.id === exportWho);
                return (
                  <div role="dialog" aria-label="Export leave" className="cal-menu cal-export" style={isMobile ? { right: 'auto', left: 0 } : undefined}>
                    <p className="cal-export-label">Whose leave</p>
                    <select
                      className="cal-export-select"
                      value={exportWho}
                      onChange={e => setExportWho(e.target.value === 'all' ? 'all' : (people.find(p => String(p.id) === e.target.value)?.id ?? 'all'))}
                      aria-label="Whose leave to export"
                    >
                      <option value="all">{deptFilter === 'All' ? 'Everyone' : `Everyone in ${deptFilter}`} ({people.length})</option>
                      <optgroup label="One team member">
                        {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </optgroup>
                    </select>

                    {exportWho === 'all' && (
                      <div className="cal-export-kind" role="radiogroup" aria-label="File layout">
                        {[
                          { key: 'requests', label: 'Every request', detail: 'One row per leave request' },
                          { key: 'summary',  label: 'Summary',       detail: 'One row per person, with totals' },
                        ].map(k => (
                          <label key={k.key} data-checked={exportKind === k.key}>
                            <input type="radio" name="cal-export-kind" checked={exportKind === k.key} onChange={() => setExportKind(k.key)} />
                            <span>
                              <span style={{ display: 'block', fontWeight: 600 }}>{k.label}</span>
                              <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-2)' }}>{k.detail}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                    {person && (
                      <p style={{ margin: '8px 2px 0', fontSize: 12, color: 'var(--text-2)', lineHeight: 1.45 }}>
                        Their requests with totals. Year files also include entitlement, used and remaining.
                      </p>
                    )}

                    <p className="cal-export-label" style={{ marginTop: 12 }}>Download</p>
                    {[
                      { range: 'month', label: format(viewDate, 'MMMM yyyy'), detail: 'The month in view' },
                      { range: 'year',  label: `All of ${viewYear}`,          detail: 'January to December' },
                    ].map(opt => (
                      <button key={opt.range} type="button" className="cal-menu-item cal-export-go" onClick={() => handleExport(opt.range)}>
                        <Download size={14} style={{ flexShrink: 0, color: 'var(--accent)' }} />
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'block', fontWeight: 600 }}>{opt.label}</span>
                          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-2)' }}>{opt.detail}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}


          <button className="cal-btn cal-btn-primary" onClick={() => { setShowForm(true); setPrefill(null); }} title="New request (N)">
            <Plus size={15} />
            {isMobile ? 'New' : 'New request'}
          </button>
        </div>
      </div>

      {/* ─── Summary: one slim line of plain text, not a row of boxes ─── */}
      <div className="cal-summary">
        <button
          type="button"
          className="cal-sum"
          onClick={goToToday}
          title={outToday.length ? outToday.map(o => o.emp.name).join(', ') : 'Jump to today'}
        >
          <Umbrella size={14} />
          {outToday.length === 0
            ? <span>Everyone's in today</span>
            : <span><b>{outToday.length}</b> out today{!isMobile && <span className="cal-sum-muted"> · {outToday.slice(0, 3).map(o => o.emp.name?.split(' ')[0]).join(', ')}{outToday.length > 3 ? ` +${outToday.length - 3}` : ''}</span>}</span>}
        </button>

        {(pendingInView > 0 || statusFocus === 'pending') && (
          <button
            type="button"
            className="cal-sum cal-sum-warn"
            aria-pressed={statusFocus === 'pending'}
            onClick={() => setStatusFocus(st => (st === 'pending' ? null : 'pending'))}
            title={statusFocus === 'pending' ? 'Show everything again' : 'Highlight requests waiting for approval'}
          >
            <Clock size={14} />
            <span><b>{pendingInView}</b> waiting for approval</span>
            {statusFocus === 'pending' && <X size={13} />}
          </button>
        )}

        <span className="cal-sum cal-sum-static">
          <CalendarDays size={14} />
          {approvedInView === 0
            ? <span>Nothing approved in {format(viewDate, 'MMMM')}</span>
            : <span><b>{approvedInView}</b> approved in {format(viewDate, 'MMMM')}</span>}
        </span>

        {nextBankHoliday && (
          <span className="cal-sum cal-sum-static cal-sum-holiday" title={`Next bank holiday: ${nextBankHoliday.name}`}>
            <Flag size={14} />
            <span>
              <b>{nextBankHoliday.name}</b>{' '}
              {nextBankHoliday.d === todayStr ? 'today' : format(parseISO(nextBankHoliday.d), 'EEE d MMM')}
              {bankHolidayIn > 0 && <span className="cal-sum-muted"> · in {bankHolidayIn} {bankHolidayIn === 1 ? 'day' : 'days'}</span>}
            </span>
          </span>
        )}
      </div>

      {/* ─── Grid ─── */}
      <div
        ref={gridCardRef}
        style={{
          background: 'var(--surface)', borderRadius: 20, border: '1px solid var(--border)', width: '100%', overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(28,26,20,0.03), 0 10px 30px rgba(28,26,20,0.04)',
          display: 'flex', flexDirection: 'column',
          maxHeight: gridMaxH ?? undefined,
        }}
      >
        {/* Key: leave types on the grid this month (all types on request).
        Sits at the top of the card so it is visible without scrolling. */}
        <div ref={keybarRef} className="cal-keybar" style={{ display: 'flex', alignItems: 'center', flexWrap: isMobile ? 'nowrap' : 'wrap', gap: isMobile ? '8px 10px' : fitMode ? '4px 12px' : '8px 14px', padding: isMobile ? '8px 10px' : fitMode ? '4px 12px' : '8px 14px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', fontSize: 13, flexShrink: 0, color: 'var(--text-2)' }}>
          {shownTypes.length === 0 && (
            <span style={{ color: 'var(--text-muted)', paddingLeft: 4 }}>No leave booked this month</span>
          )}
          {shownTypes.map(({ type, cfg, approved }) => {
            const Icon = cfg.icon;
            const on = focusType === type;
            return (
              <button
                key={type}
                type="button"
                className={'cal-key' + (focusType && !on ? ' cal-key-dim' : '')}
                aria-pressed={on}
                onClick={() => setFocusType(on ? null : type)}
                title={on ? 'Show every leave type' : `Highlight ${type} (${approved} approved this month)`}
              >
                <span style={{ width: 20, height: 20, borderRadius: 6, background: cfg.approved, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={11} color={readableOn(cfg.approved)} strokeWidth={2.4} />
                </span>
                {type}
                {approved > 0 && <span className="cal-pill" style={{ height: 18, padding: '0 6px', background: on ? 'var(--accent)' : 'var(--border)', color: on ? 'var(--on-accent)' : 'var(--text)' }}>{approved}</span>}
              </button>
            );
          })}
          {hiddenTypeCount > 0 && (
            <button type="button" className="cal-key cal-key-more" onClick={() => setShowAllTypes(v => !v)}>
              {showAllTypes ? 'Fewer types' : `+${hiddenTypeCount} more ${hiddenTypeCount === 1 ? 'type' : 'types'}`}
            </button>
          )}
          {focusType && (
            <button type="button" className="cal-key cal-key-more" onClick={() => setFocusType(null)}>
              <X size={13} /> Show all
            </button>
          )}
          <span style={{ flex: 1 }} />
          <span className="cal-patterns" style={{ display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', color: 'var(--text-muted)', fontSize: 12.5 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}>
              <span style={{ width: 16, height: 12, borderRadius: 4, background: 'var(--tint)', border: '1.5px dashed var(--accent)', boxSizing: 'border-box' }} />
              Pending
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}>
              <span style={{ width: 16, height: 12, borderRadius: 4, background: 'linear-gradient(90deg, var(--text-2) 0 50%, var(--border) 50% 100%)' }} />
              Half day
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}>
              <span style={{ width: 16, height: 12, borderRadius: 4, border: '1px solid var(--border)', boxSizing: 'border-box', backgroundImage: 'repeating-linear-gradient(135deg, rgba(140,135,121,0.4) 0 1.5px, transparent 1.5px 5px)' }} />
              Non-working
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}>
              <span style={{ width: 16, height: 12, borderRadius: 4, backgroundColor: 'var(--holiday-bg)', backgroundImage: 'repeating-linear-gradient(135deg, var(--holiday-stripe) 0 2px, transparent 2px 8px)', border: '1px solid var(--holiday-edge)', boxShadow: 'inset 0 2px 0 var(--holiday-strong)', boxSizing: 'border-box' }} />
              Bank holiday
            </span>
          </span>
        </div>
        <div ref={scrollRef} className="cal-scroll" style={{ overflowX: 'auto', overflowY: gridMaxH ? 'auto' : 'visible', flex: '1 1 auto', minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
          <div
            key={animKey}
            style={{
              minWidth: gridMinWidth,
              animation: slideDir ? 'slideIn-' + slideDir + ' 0.28s cubic-bezier(0.22, 1, 0.36, 1) both' : 'none',
            }}
          >
            {/* Day header — stays at the top while the team scrolls underneath */}
            <div style={{ display: 'flex', height: HEADER_H, boxSizing: 'border-box', borderBottom: '1px solid var(--border)', background: 'var(--surface)', position: 'sticky', top: 0, zIndex: 35 }}>
              <div style={{ width: LEFT_COL, minWidth: LEFT_COL, flexShrink: 0, display: 'flex', alignItems: 'center', padding: isMobile ? '0 16px' : '0 20px', borderRight: '1px solid var(--border)', height: '100%', position: 'sticky', left: 0, zIndex: 30, background: 'var(--surface)', boxSizing: 'border-box' }}>
                <div style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {deptFilter === 'All' ? 'Team' : deptFilter}
                  </span>
                  <span style={{ display: 'block', marginTop: 3, fontSize: 13, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {filteredEmps.length} {filteredEmps.length === 1 ? 'person' : 'people'}
                    {query.trim() && <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}> matching</span>}
                  </span>
                </div>
              </div>
              <div style={{ flex: 1, display: 'flex' }}>
                {dayInfo.map(({ day, key, isWk, isToday, ph: anyPh, flex, minW }, di) => {
                  // A holiday someone in view actually gets off (their location, not opted out)
                  const ph = anyPh
                    ? filteredEmps.reduce((found, emp) => found || (empObservesHolidays(allowancesMap[emp.id])
                        ? getPublicHoliday(empHolidaysById[emp.id] ?? [], day) : null), null)
                    : null;
                  const anyObserves = !!ph;
                  const bdayEmps    = filteredEmps.filter(emp => isBirthday(profilesMap[emp.id], day));
                  const away        = anyObserves ? [] : (awayByDay[di] ?? []);
                  const crowded     = away.length >= crowdedAt;
                  const notes = [
                    anyObserves ? ph.name : null,
                    away.length ? `${away.length} away: ${away.slice(0, 8).join(', ')}${away.length > 8 ? ` +${away.length - 8}` : ''}` : null,
                    bdayEmps.length ? bdayEmps.map(e => e.name).join(', ') + (bdayEmps.length === 1 ? "'s birthday" : ' have birthdays') : null,
                  ].filter(Boolean).join(' – ');
                  return (
                    <div
                      key={key}
                      data-today={isToday ? 'true' : undefined}
                      title={notes || undefined}
                      style={{
                        flex, minWidth: minW, height: '100%', display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', gap: compact ? 3 : 5, position: 'relative', boxSizing: 'border-box',
                        borderLeft: anyObserves ? '1px solid var(--holiday-edge)' : di > 0 && day.getDay() === 1 ? '1px solid var(--border-soft)' : 'none',
                        borderRight: anyObserves ? '1px solid var(--holiday-edge)' : 'none',
                        background: anyObserves ? 'var(--holiday-bg)' : isWk ? 'var(--weekend)' : isToday ? 'var(--today-cell)' : 'transparent',
                        boxShadow: anyObserves ? 'inset 0 3px 0 var(--holiday-strong)' : 'none',
                      }}
                    >
                      {anyObserves ? (
                        <Flag size={11} strokeWidth={2.4} style={{ color: 'var(--holiday-ink)' }} aria-label={ph.name} />
                      ) : (
                        <span style={{ fontSize: 11, fontWeight: 500, lineHeight: 1, letterSpacing: '0.02em', color: isToday ? 'var(--accent)' : isWk ? 'var(--text-faint)' : 'var(--text-muted)' }}>
                          {format(day, 'EEEEE')}
                        </span>
                      )}
                      <span style={{
                        minWidth: isWk && !anyObserves ? 22 : 28, height: isWk && !anyObserves ? 22 : 28, padding: '0 2px', boxSizing: 'border-box', borderRadius: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: isWk && !anyObserves ? 12 : 13.5, fontWeight: isToday || anyObserves ? 700 : 500, lineHeight: 1,
                        background: isToday ? 'var(--accent)' : anyObserves ? 'var(--holiday-strong)' : 'transparent',
                        color: isToday ? 'var(--on-accent)' : anyObserves ? '#1C1A14' : isWk ? 'var(--text-faint)' : 'var(--text)',
                      }}>
                        {format(day, 'd')}
                      </span>
                      {away.length > 0 && (
                        <span className={'cal-away' + (crowded ? ' cal-away-crowded' : '')} aria-label={`${away.length} away`}>
                          {away.length}
                        </span>
                      )}
                      {bdayEmps.length > 0 && <span style={{ position: 'absolute', top: 5, right: 5, width: 5, height: 5, borderRadius: '50%', background: '#c98a00' }} />}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* People */}
            {filteredEmps.length === 0 && (
              <div style={{ position: 'sticky', left: 0, width: 'min(100%, 100vw)', padding: '48px 20px', textAlign: 'center', fontSize: 14, color: 'var(--text-2)' }}>
                {query.trim()
                  ? <>No one matches “{query.trim()}”. <button onClick={() => setQuery('')} style={{ border: 'none', background: 'none', padding: 0, color: 'var(--accent)', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Clear search</button></>
                  : 'No one to show in this department yet.'}
              </div>
            )}

            {groups.map((group, gi) => (
              <React.Fragment key={group.dept ?? 'all'}>
                {group.dept && (() => {
                  const isCollapsed = collapsed.has(group.dept);
                  const off = isViewingThisMonth ? offToday(group.emps) : 0;
                  return (
                    <div style={{ display: 'flex', height: GROUP_H, boxSizing: 'border-box', background: 'var(--surface-2)', borderBottom: isCollapsed ? 'none' : '1px solid var(--border-soft)', borderTop: gi > 0 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ width: isMobile ? 'max-content' : LEFT_COL, minWidth: LEFT_COL, maxWidth: isMobile ? '85vw' : LEFT_COL, boxSizing: 'border-box', flexShrink: 0, padding: isMobile ? '0 16px' : '0 20px 0 18px', position: 'sticky', left: 0, zIndex: 20, background: 'var(--surface-2)', borderRight: isMobile ? 'none' : '1px solid var(--border)' }}>
                        <button
                          type="button"
                          className="cal-group-btn"
                          onClick={() => toggleGroup(group.dept)}
                          aria-expanded={!isCollapsed}
                          title={isCollapsed ? `Show ${group.dept}` : `Hide ${group.dept}`}
                        >
                          <ChevronDown size={15} style={{ color: 'var(--text-muted)', flexShrink: 0, transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.15s' }} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{group.dept}</span>
                          <span className="cal-pill" style={{ background: 'var(--border)', color: 'var(--text-2)' }}>{group.emps.length}</span>
                          {off > 0 && <span className="cal-pill" style={{ background: 'var(--tint)', color: 'var(--accent)', marginLeft: 'auto' }}>{off} off today</span>}
                        </button>
                      </div>
                      <div style={{ flex: 1 }} />
                    </div>
                  );
                })()}
                {!collapsed.has(group.dept) && group.emps.map((emp, i) => renderRow(emp, gi === groups.length - 1 && i === group.emps.length - 1))}
              </React.Fragment>
            ))}
          </div>
        </div>

      </div>

      {(showForm || prefill) && (
        <RequestForm
          prefill={prefill}
          onClose={() => { setShowForm(false); setPrefill(null); }}
          onRequestCreated={handleRequestAdded}
        />
      )}

      {selectedLeave && (
        <LeaveDetailPopup
          leave={selectedLeave.leave}
          emp={selectedLeave.emp}
          currentUser={currentUser}
          leaveColors={leaveColors}
          otherHalfAlreadyBooked={(selectedLeave.allEntries ?? []).length >= 2}
          onClose={() => setSelectedLeave(null)}
          onDelete={handleDeleteFromPopup}
          deleting={deletingId === selectedLeave.leave.id}
          onRequestOtherHalf={(otherPeriod) => {
            const { leave, emp } = selectedLeave;
            setSelectedLeave(null);
            setPrefill({ from: String(leave.from).slice(0, 10), to: String(leave.to ?? leave.from).slice(0, 10), empId: emp.id, empName: emp.name, halfDay: true, halfDayPeriod: otherPeriod });
          }}
        />
      )}

      {/* ─── Admin/manager hover summary card ─── */}
      {hoveredEmp && hoverEnabled && balancesReady && (
        <EmployeeHoverCard
          emp={hoveredEmp.emp}
          top={hoveredEmp.top}
          left={hoveredEmp.left}
          stats={computeEmployeeLeaveStats(hoveredEmp.emp, calendarRequests, allowancesMap, schedulesMap, leaveColors, viewYear, publicHolidays)}
          leaveColors={leaveColors}
          publicHolidays={publicHolidays}
          viewDate={viewDate}
          onMouseEnter={cancelHideHover}
          onMouseLeave={scheduleHideHover}
          onDownload={isPrivilegedUser ? handlePersonDownload : undefined}
        />
      )}
    </div>
  );
}